<template>
  <div>
    <loading 
      :active.sync="loading"
      :can-cancel="false"
      background-color="#fff"
      :is-full-page="false">
    </loading>
    
    <div v-if="service">
      <!-- Card con la información del servicio -->
      <b-card class="mb-3 service-detail-card">
        <b-card-body class="pt-3 px-3 pb-2">
          <!-- Primera fila: counter, mascota y estado -->
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex align-items-center flex-grow-1">
              <span 
                class="service-counter mr-2"
                :style="{ backgroundColor: service.estado_color, color: '#fff' }">
                {{ serviceIndex }}
              </span>
              <h6 class="mb-0 font-weight-bold">{{ service.mascota }}</h6>
            </div>
            <CBadge 
              :style="{ backgroundColor: service.estado_color, color: '#fff', border: 'none' }"
              class="ml-2">
              {{ service.estado }}
            </CBadge>
          </div>
          <!-- Segunda fila: cliente y tipo_servicio -->
          <div class="d-flex align-items-center mb-1 text-muted">
            <span class="mr-3">{{ service.cliente }}</span>
            <span>{{ service.tipo_servicio }}</span>
          </div>
          <div class="d-flex align-items-center text-muted mb-1">
            <span>{{ service.barrio }} - {{ service.direccion }}</span>
          </div>
          <div class="d-flex align-items-center text-muted mb-1">
            <span class="mr-3">{{ service.dia }}</span>
            <span>{{ service.hora_inicio }} - {{ service.hora_fin }}</span>
          </div>

          <!-- Botones de acción -->
          <div class="d-flex align-items-center mb-0">
            <b-button 
              variant="outline-secondary" 
              @click="refreshService"
              :disabled="loading"
              size="sm"
              class="mr-2">
              Refrescar
            </b-button>
            <b-button 
              v-if="service.estado_id === 1 && userProfile"
              :style="{ backgroundColor: '#336535', color: '#fff', borderColor: '#336535' }"
              @click="initiateService"
              :disabled="loading"
              size="sm"
              class="mr-2">
              Iniciar
            </b-button>
            <b-button 
              v-if="service.estado_id === 2 && userProfile"
              :style="{ backgroundColor: '#bf0811', color: '#fff', borderColor: '#bf0811' }"
              @click="initiateService"
              :disabled="loading || !canFinalizeService"
              size="sm"
              class="mr-2">
              Finalizar
            </b-button>
            <!-- Botón de prueba para Check In -->
            <b-button 
              v-if="shouldShowCheckIn"
              :style="{ backgroundColor: '#17a2b8', color: '#fff', borderColor: '#17a2b8' }"
              @click="showCheckInModal = true"
              :disabled="loading"
              size="sm"
              class="mr-2">
              Check In
            </b-button>
            <!-- Botón de Checkout -->
            <b-button 
              v-if="shouldShowCheckOut"
              :style="{ backgroundColor: '#ff9800', color: '#fff', borderColor: '#ff9800' }"
              @click="showCheckOutModal = true"
              :disabled="loading"
              size="sm"
              class="mr-2">
              Check Out
            </b-button>
            <!-- Botón de Evidencia -->
            <b-button 
              v-if="shouldShowEvidence"
              :style="{ backgroundColor: '#9c27b0', color: '#fff', borderColor: '#9c27b0' }"
              @click="showEvidenceModal = true"
              :disabled="loading"
              size="sm">
              Evidencia
            </b-button>
          </div>
        </b-card-body>
      </b-card>

      <!-- Aquí puedes agregar más detalles del servicio -->
      <b-card>
        <b-card-header>
          <h5>Detalles del servicio</h5>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col md="6">
              <p class="d-flex align-items-center">
                <strong class="mr-2">ID:</strong> 
                <span class="mr-2">{{ service.id }}</span>
                <b-button
                  variant="link"
                  size="sm"
                  @click="copyServiceId"
                  class="p-0"
                  title="Copiar ID al portapapeles"
                >
                  <CIcon name="cilCopy" class="text-primary"/>
                </b-button>
              </p>
              <p><strong>Fecha:</strong> {{ service.fecha }}</p>
              <p><strong>Servicio detalle:</strong> {{ service.servicio_detalle }}</p>
              <p>
                <strong>CheckIn realizado:</strong> 
                <span v-if="service.check_in_done" class="checkin-status">
                  <CIcon name="cilCheck" class="checkin-check-icon-green mr-1"/>
                  <a href="#" @click.prevent="showCheckInDetailModal = true" class="checkin-detail-link">
                    Ver detalle
                  </a>
                </span>
                <span v-else>No</span>
              </p>
              <p v-if="service.estado_id === 2" class="d-flex align-items-center flex-wrap">
                <strong class="mr-2">GPS:</strong> 
                <CIcon 
                  :name="gpsPosition ? 'cilCheckCircle' : 'cilXCircle'" 
                  :class="gpsPosition ? 'text-success' : 'text-danger'"
                  class="mr-1"
                />
                <span :class="gpsPosition ? 'text-success' : 'text-danger'" class="mr-2">
                  {{ gpsPosition ? 'Coordenadas obtenidas' : 'Sin coordenadas' }}
                </span>
                <small v-if="gpsPosition" class="text-muted mr-2" style="font-size: 0.75rem;">
                  ({{ gpsPosition.lat.toFixed(6) }}, {{ gpsPosition.long.toFixed(6) }})
                </small>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="getGPSOnDemand"
                  :disabled="loading || isGettingGPS"
                  class="p-1 mr-1"
                  style="font-size: 0.75rem;"
                >
                  <span v-if="isGettingGPS">
                    <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                    Obteniendo...
                  </span>
                  <span v-else>
                    <CIcon name="cilLocationPin" class="mr-1"/> Obtener GPS
                  </span>
                </b-button>
                <b-button
                  v-if="gpsPosition"
                  variant="primary"
                  size="sm"
                  @click="sendGPSManually"
                  :disabled="loading || isSendingGPS"
                  class="p-1"
                  style="font-size: 0.75rem;"
                >
                  <span v-if="isSendingGPS">
                    <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                    Enviando...
                  </span>
                  <span v-else>
                    Enviar GPS
                  </span>
                </b-button>
              </p>
            </b-col>
            <b-col md="6">
              <p><strong>Tipo inicio:</strong> {{ service.tipo_inicio || 'N/A' }}</p>
              <p><strong>Duración:</strong> {{ service.duracion }} {{ service.tipo_duracion || '' }}</p>
              <p><strong>Distancia:</strong> {{ service.distancia || 'N/A' }}</p>
              <p v-if="service.last_service">
                <strong>CheckOut realizado:</strong> 
                <span v-if="hasCheckOut" class="checkin-status">
                  <CIcon name="cilCheck" class="checkin-check-icon-green mr-1"/>
                  <a href="#" @click.prevent="showCheckOutDetailModal = true" class="checkin-detail-link">
                    Ver detalle
                  </a>
                </span>
                <span v-else>No</span>
              </p>
            </b-col>
          </b-row>
          <b-row v-if="service.comentario && service.comentario !== 'NO'">
            <b-col cols="12">
              <p><strong>Comentario:</strong> {{ service.comentario }}</p>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Recorrido del paseo: mapa en estado 1 (ubicación cliente), 2 (en curso) y 34 (finalizado, evidencia) -->
      <b-card v-if="showRecorridoMap" class="mt-3">
        <b-card-header>
          <h5 class="mb-0">
            {{ isRecorridoProgramado ? 'Ubicación del cliente' : 'Recorrido del paseo' }}
            {{ isRecorridoSoloVista ? '(finalizado)' : '' }}
          </h5>
          <p class="text-muted small mb-0 mt-1">
            <template v-if="isRecorridoProgramado">
              Ubicación donde debes iniciar el servicio para que te guíes.
            </template>
            <template v-else>
              <strong>Distancia:</strong> {{ service.distancia != null && service.distancia !== '' ? service.distancia + ' km' : '0 km' }}
              <span class="ml-2">·</span>
              <span class="ml-2">{{ recorridoPath.length }} punto(s) de ubicación</span>
              <span v-if="!isRecorridoSoloVista && gpsPeriodicCountdownSeconds > 0" class="ml-2">
                · <strong>Próximo envío en {{ formatGpsCountdown(gpsPeriodicCountdownSeconds) }}</strong>
              </span>
              <span v-if="isRecorridoSoloVista" class="ml-2 text-muted">· Solo vista</span>
            </template>
          </p>
        </b-card-header>
        <b-card-body>
          <div class="recorrido-map-container" style="height: 280px; width: 100%; border-radius: 8px; overflow: hidden;">
            <gmap-map
              :center="recorridoMapCenter"
              :zoom="recorridoPath.length > 0 ? 15 : 14"
              style="height: 100%; width: 100%;"
              :options="{
                mapTypeControl: true,
                zoomControl: true,
                streetViewControl: false,
                fullscreenControl: true
              }"
            >
              <gmap-polyline
                v-if="recorridoPath.length > 0"
                :path="recorridoPath"
                :options="{ strokeColor: '#336535', strokeWeight: 4 }"
              />
              <gmap-marker
                v-if="recorridoHomePosition"
                :position="recorridoHomePosition"
                title="Casa del cliente"
                :icon="{
                  url: require('@/assets/images/casa-de-perro32.png'),
                  scaledSize: { width: 32, height: 32 }
                }"
              />
              <gmap-marker
                v-if="recorridoLastPosition"
                :position="recorridoLastPosition"
                title="Última ubicación"
                :icon="{
                  url: require('@/assets/images/huella24.png'),
                  scaledSize: { width: 28, height: 28 }
                }"
                :animation="recorridoPawMarkerAnimation"
              />
            </gmap-map>
          </div>
          <p v-if="isRecorridoSoloVista" class="text-muted small mt-2 mb-0">
            Servicio finalizado. El mapa queda como evidencia de cómo ejecutaste el servicio.
          </p>
          <p v-else-if="isRecorridoProgramado" class="text-muted small mt-2 mb-0">
            Punto de recogida del cliente. Al iniciar el paseo se irá dibujando el recorrido.
          </p>
          <p v-else-if="recorridoPath.length === 0" class="text-muted small mt-2 mb-0">
            El recorrido se irá dibujando al enviar ubicaciones (inicio del paseo y cada 4 min, o con «Enviar GPS»).
          </p>
        </b-card-body>
      </b-card>

      <!-- Bitácora: WFStates + evidencias en orden cronológico; referencia hora_inicio para ver si iniciaron tarde -->
      <b-card v-if="service.evidencias || (service.WFStates && service.WFStates.length)" class="mt-3 bitacora-card">
        <b-card-header class="bitacora-header">
          <h5 class="mb-0 bitacora-title">Bitácora del servicio</h5>
          <p class="mb-0 mt-1 small text-white" style="opacity: 0.95;">
            Inicio programado: <strong>{{ service.hora_inicio || '—' }}</strong>
            <span class="ml-2">·</span>
            Fin programado: <strong>{{ service.hora_fin || '—' }}</strong>
          </p>
        </b-card-header>
        <b-card-body class="bitacora-body">
          <div v-if="bitacoraItems.length > 0" class="bitacora-timeline">
            <div
              v-for="(item, index) in bitacoraItems"
              :key="index"
              class="bitacora-item"
            >
              <div class="bitacora-icon" :class="item.iconClass">
                <CIcon :name="item.icon" />
              </div>
              <div class="bitacora-content">
                <div class="bitacora-time">{{ item.time }}</div>
                <div class="bitacora-label">
                  <span v-if="item.emoji" class="bitacora-emoji">{{ item.emoji }}</span>
                  <span v-if="item.isCheckIn" @click="showCheckInDetailModal = true" class="bitacora-photo-link">{{ item.label }}</span>
                  <span v-else-if="item.hasPhoto" @click="openImageFullscreen(item.photoUrl, item.label)" class="bitacora-photo-link">
                    {{ item.label }}
                    <CIcon name="cilImage" class="ml-1" style="font-size: 0.875rem;"/>
                  </span>
                  <span v-else>{{ item.label }}</span>
                </div>
                <div v-if="item.videoLink" class="bitacora-link">
                  <a href="#" @click.prevent="openVideoFullscreen(item.videoUrl, item.label)">Ver video</a>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="bitacora-empty">
            <p class="text-muted mb-0">No hay actividades registradas aún</p>
          </div>
        </b-card-body>
      </b-card>

      <!-- Sección de Evidencias -->
      <b-card v-if="service.evidencias && service.evidencias.length > 0" class="mt-3">
        <b-card-header>
          <h5>Evidencias del Servicio</h5>
        </b-card-header>
        <b-card-body>
          <div class="evidence-list">
            <div 
              v-for="evidence in nonCheckInEvidences" 
              :key="evidence.id"
              class="evidence-item mb-3"
            >
              <div class="evidence-item-header">
                <div class="evidence-item-info">
                  <strong class="evidence-label">{{ (evidence.evidence[0] || {}).label || 'Sin etiqueta' }}</strong>
                  <small class="text-muted d-block">{{ formatDate(evidence.created_at) }}</small>
                </div>
                <div v-if="(evidence.evidence[0] || {}).photo" class="evidence-photo-thumbnail" @click="openImageFullscreen(evidence.evidence[0].photo, evidence.evidence[0].label)">
                  <img 
                    :src="evidence.evidence[0].photo" 
                    alt="Evidencia"
                    class="evidence-thumbnail-img"
                  />
                  <div class="evidence-thumbnail-overlay">
                    <CIcon name="cilZoom" class="text-white"/>
                  </div>
                </div>
                <div v-else-if="(evidence.evidence[0] || {}).video" class="evidence-photo-thumbnail" @click="openVideoFullscreen(evidence.evidence[0].video, evidence.evidence[0].label)">
                  <video 
                    :src="evidence.evidence[0].video" 
                    class="evidence-thumbnail-img"
                    muted
                  ></video>
                  <div class="evidence-thumbnail-overlay">
                    <CIcon name="cilMediaPlay" class="text-white"/>
                  </div>
                </div>
                <div v-else class="evidence-no-photo">
                  <CIcon name="cilImage" class="text-muted"/>
                </div>
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </div>

    <div v-else class="text-center">
      <p>No se encontró información del servicio</p>
      <b-button @click="goBack">Volver</b-button>
    </div>

    <!-- Modal de Check In -->
    <b-modal
      v-model="showCheckInModal"
      title="Registro de Artículos"
      size="lg"
      @ok="handleCheckInSubmit"
      @hidden="resetCheckInForm"
      :ok-disabled="!hasSelectedItems || isUploadingCheckIn"
      ok-title="Continuar"
      cancel-title="Cancelar"
      header-class="checkin-modal-header"
      body-class="checkin-modal-body"
      footer-class="checkin-modal-footer"
      hide-header-close
    >
      <div class="checkin-form">
        <p class="checkin-description mb-3">
          Selecciona los artículos recibidos y, opcionalmente, agrega una foto o video de cada uno.
          <span class="text-danger">*</span> <small class="text-muted">Mascota es obligatorio (con foto o video)</small>
        </p>
        
        <!-- Lista de opciones en cards estilo Material Design -->
        <div class="checkin-options mb-4">
          <div 
            v-for="option in checkInOptions" 
            :key="option.value"
            class="checkin-card"
            :class="{ 'checkin-card-selected': selectedCheckInItems.includes(option.value) }"
          >
            <div class="checkin-card-content">
              <div class="checkin-card-left">
                <b-form-checkbox
                  v-model="selectedCheckInItems"
                  :value="option.value"
                  @change="onCheckInItemChange(option.value)"
                  class="checkin-checkbox"
                >
                  <span class="checkin-label">
                    {{ option.label }}
                    <span v-if="option.value === 'mascota'" class="text-danger ml-1" style="font-size: 0.875rem;">*</span>
                  </span>
                </b-form-checkbox>
                
                <!-- Indicador de foto/video agregada -->
                <div v-if="selectedCheckInItems.includes(option.value) && (checkInPhotoPreviews[option.value] || checkInVideoPreviews[option.value])" class="checkin-photo-indicator">
                  <CIcon name="cilCheckCircle" class="checkin-check-icon"/>
                  <span class="checkin-photo-text">
                    {{ checkInPhotoPreviews[option.value] ? 'Foto agregada' : 'Video agregado' }}
                  </span>
                </div>
                
                <!-- Campo de texto para "otro" -->
                <b-form-input
                  v-if="option.value === 'otro' && selectedCheckInItems.includes(option.value)"
                  v-model="checkInOtherText"
                  placeholder="Juguete favorito"
                  class="checkin-custom-input mt-2"
                  size="sm"
                ></b-form-input>
              </div>
              
              <!-- Vista previa de foto/video o botón para agregar -->
              <div class="checkin-card-right">
                <!-- Vista previa de foto capturada -->
                <div v-if="checkInPhotoPreviews[option.value]" class="checkin-photo-thumbnail">
                  <img 
                    :src="checkInPhotoPreviews[option.value]" 
                    alt="Vista previa"
                    class="checkin-thumbnail-img"
                  />
                  <div class="checkin-thumbnail-overlay">
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeCheckInMedia(option.value)"
                      class="checkin-thumbnail-btn"
                      title="Eliminar foto"
                    >
                      <CIcon name="cilTrash" class="text-white"/>
                    </b-button>
                  </div>
                </div>
                
                <!-- Vista previa de video capturado -->
                <div v-else-if="checkInVideoPreviews[option.value]" class="checkin-photo-thumbnail">
                  <video 
                    :src="checkInVideoPreviews[option.value]" 
                    class="checkin-thumbnail-img"
                    controls
                  ></video>
                  <div class="checkin-thumbnail-overlay">
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeCheckInMedia(option.value)"
                      class="checkin-thumbnail-btn"
                      title="Eliminar video"
                    >
                      <CIcon name="cilTrash" class="text-white"/>
                    </b-button>
                  </div>
                </div>
                
                <!-- Botones para agregar foto o video (solo si está seleccionado) -->
                <div v-else-if="selectedCheckInItems.includes(option.value)" class="checkin-add-photo">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="openCamera(option.value, 'photo')"
                    class="checkin-add-photo-btn mr-1"
                    :disabled="isCapturingPhoto || isRecordingVideo || activeCameraOption === option.value"
                  >
                    <CIcon name="cilCamera" class="mr-1"/> 
                    <span v-if="activeCameraOption === option.value">Abriendo...</span>
                    <span v-else>Foto</span>
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="openCamera(option.value, 'video')"
                    class="checkin-add-photo-btn"
                    :disabled="isCapturingPhoto || isRecordingVideo || activeCameraOption === option.value"
                  >
                    <CIcon name="cilVideo" class="mr-1"/> 
                    <span v-if="activeCameraOption === option.value">Abriendo...</span>
                    <span v-else>Video</span>
                  </b-button>
                </div>
                
                <!-- Icono de cámara placeholder cuando no está seleccionado -->
                <div v-else class="checkin-camera-placeholder">
                  <CIcon name="cilCamera" class="checkin-placeholder-icon"/>
                </div>
              </div>
            </div>
            
            <!-- Vista previa de la cámara cuando está abierta -->
            <div v-if="activeCameraOption === option.value && !checkInPhotoPreviews[option.value] && !checkInVideoPreviews[option.value]" class="checkin-camera-preview-container mt-3">
              <video
                :ref="`camera-${option.value}`"
                autoplay
                playsinline
                muted
                class="checkin-camera-preview"
              ></video>
              <div class="checkin-camera-controls mt-2">
                <template v-if="activeCameraMode === 'photo'">
                  <b-button
                    variant="success"
                    size="sm"
                    @click="capturePhoto(option.value)"
                    class="mr-2"
                  >
                    <CIcon name="cilCheck" class="mr-1"/> Capturar Foto
                  </b-button>
                </template>
                <template v-else-if="activeCameraMode === 'video'">
                  <b-button
                    v-if="!isRecordingVideo"
                    variant="danger"
                    size="sm"
                    @click="startRecordingVideo(option.value)"
                    class="mr-2"
                  >
                    <CIcon name="cilMediaRecord" class="mr-1"/> Iniciar Grabación
                  </b-button>
                  <b-button
                    v-else
                    variant="danger"
                    size="sm"
                    @click="stopRecordingVideo(option.value)"
                    class="mr-2"
                  >
                    <CIcon name="cilMediaStop" class="mr-1"/> Detener
                  </b-button>
                </template>
                <b-button
                  variant="secondary"
                  size="sm"
                  @click="closeCamera(option.value)"
                >
                  <CIcon name="cilXCircle" class="mr-1"/> Cancelar
                </b-button>
              </div>
            </div>
          </div>
        </div>
        
        <b-alert v-if="checkInError" variant="danger" dismissible @dismissed="checkInError = ''">
          {{ checkInError }}
        </b-alert>
      </div>
      
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="isUploadingCheckIn">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok()" 
          :disabled="!hasSelectedItems || isUploadingCheckIn"
          class="checkin-continue-btn"
        >
          <CIcon name="cilCheck" class="mr-1"/>
          <span v-if="isUploadingCheckIn">
            <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Guardando...
          </span>
          <span v-else>Continuar</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Modal de Evidencia -->
    <b-modal
      v-model="showEvidenceModal"
      title="Agregar Evidencia"
      size="lg"
      @ok="handleEvidenceSubmit"
      @hidden="resetEvidenceForm"
      :ok-disabled="!evidenceComment.trim() || isUploadingEvidence"
      ok-title="Guardar"
      cancel-title="Cancelar"
      header-class="checkin-modal-header"
      body-class="checkin-modal-body"
      footer-class="checkin-modal-footer"
      hide-header-close
    >
      <div class="evidence-form">
        <p class="checkin-description mb-3">
          Agrega un comentario y opcionalmente una foto o video como evidencia del servicio.
        </p>
        
        <!-- Campo de comentario -->
        <b-form-group label="Comentario" label-for="evidence-comment" class="mb-3">
          <b-form-textarea
            id="evidence-comment"
            v-model="evidenceComment"
            placeholder="Escribe un comentario sobre la evidencia..."
            rows="4"
            required
          ></b-form-textarea>
        </b-form-group>
        
        <!-- Campo de foto/video -->
        <b-form-group label="Foto o Video (Opcional)" label-for="evidence-media">
          <!-- Botones para abrir la cámara -->
          <div v-if="!evidencePhotoPreview && !evidenceVideoPreview && !activeEvidenceCamera" class="mb-2">
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="openEvidenceCamera('photo')"
              :disabled="isCapturingEvidencePhoto || isRecordingEvidenceVideo"
              class="mr-2"
            >
              <CIcon name="cilCamera" class="mr-1"/> 
              {{ isCapturingEvidencePhoto ? 'Abriendo cámara...' : 'Tomar foto' }}
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="openEvidenceCamera('video')"
              :disabled="isCapturingEvidencePhoto || isRecordingEvidenceVideo"
            >
              <CIcon name="cilVideo" class="mr-1"/> 
              {{ isRecordingEvidenceVideo ? 'Abriendo cámara...' : 'Grabar video' }}
            </b-button>
          </div>
          
          <!-- Vista previa de la cámara -->
          <div v-if="activeEvidenceCamera && !evidencePhotoPreview && !evidenceVideoPreview" class="evidence-camera-preview-container mb-2">
            <video
              ref="evidence-camera"
              autoplay
              playsinline
              muted
              class="checkin-camera-preview"
            ></video>
            <div class="checkin-camera-controls mt-2">
              <template v-if="activeEvidenceCameraMode === 'photo'">
                <b-button
                  variant="success"
                  size="sm"
                  @click="captureEvidencePhoto"
                  class="mr-2"
                >
                  <CIcon name="cilCheck" class="mr-1"/> Capturar Foto
                </b-button>
              </template>
              <template v-else-if="activeEvidenceCameraMode === 'video'">
                <b-button
                  v-if="!isRecordingEvidenceVideo"
                  variant="danger"
                  size="sm"
                  @click="startRecordingEvidenceVideo"
                  class="mr-2"
                >
                  <CIcon name="cilMediaRecord" class="mr-1"/> Iniciar Grabación
                </b-button>
                <b-button
                  v-else
                  variant="danger"
                  size="sm"
                  @click="stopRecordingEvidenceVideo"
                  class="mr-2"
                >
                  <CIcon name="cilMediaStop" class="mr-1"/> Detener
                </b-button>
              </template>
              <b-button
                variant="secondary"
                size="sm"
                @click="closeEvidenceCamera"
              >
                <CIcon name="cilXCircle" class="mr-1"/> Cancelar
              </b-button>
            </div>
          </div>
          
          <!-- Vista previa de la foto capturada -->
          <div v-if="evidencePhotoPreview" class="evidence-photo-preview mb-2">
            <img 
              :src="evidencePhotoPreview" 
              alt="Vista previa"
              class="checkin-photo-preview"
            />
            <div class="mt-2">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="removeEvidenceMedia"
                class="mr-2"
              >
                Eliminar foto
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="openEvidenceCamera('photo')"
                class="mr-2"
              >
                <CIcon name="cilCamera" class="mr-1"/> Tomar otra
              </b-button>
            </div>
          </div>
          
          <!-- Vista previa del video capturado -->
          <div v-if="evidenceVideoPreview" class="evidence-photo-preview mb-2">
            <video 
              :src="evidenceVideoPreview" 
              class="checkin-photo-preview"
              controls
            ></video>
            <div class="mt-2">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="removeEvidenceMedia"
                class="mr-2"
              >
                Eliminar video
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="openEvidenceCamera('video')"
              >
                <CIcon name="cilVideo" class="mr-1"/> Grabar otro
              </b-button>
            </div>
          </div>
        </b-form-group>
        
        <b-alert v-if="evidenceError" variant="danger" dismissible @dismissed="evidenceError = ''">
          {{ evidenceError }}
        </b-alert>
      </div>
      
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="isUploadingEvidence">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok()" 
          :disabled="!evidenceComment.trim() || isUploadingEvidence"
          class="checkin-continue-btn"
        >
          <span v-if="isUploadingEvidence">
            <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Guardando...
          </span>
          <span v-else>Guardar</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Modal de Check Out -->
    <b-modal
      v-model="showCheckOutModal"
      title="Check Out - Registro de Artículos"
      size="lg"
      @ok="handleCheckOutSubmit"
      @hidden="resetCheckOutForm"
      :ok-disabled="!hasAllCheckOutMedia || isUploadingCheckOut"
      ok-title="Continuar"
      cancel-title="Cancelar"
      header-class="checkin-modal-header"
      body-class="checkin-modal-body"
      footer-class="checkin-modal-footer"
      hide-header-close
    >
      <div class="checkin-form">
        <p class="checkin-description mb-3">
          Agrega una foto o video obligatoria para cada artículo recibido en el check-in.
          <span class="text-danger">*</span> <small class="text-muted">Todos los artículos requieren foto o video</small>
        </p>
        
        <!-- Lista de opciones en cards estilo Material Design -->
        <div class="checkin-options mb-4">
          <div 
            v-for="label in checkOutItems" 
            :key="label"
            class="checkin-card checkin-card-selected"
          >
            <div class="checkin-card-content">
              <div class="checkin-card-left">
                <span class="checkin-label">
                  {{ label }}
                  <span class="text-danger ml-1" style="font-size: 0.875rem;">*</span>
                </span>
                
                <!-- Indicador de foto/video agregada -->
                <div v-if="checkOutPhotoPreviews[label] || checkOutVideoPreviews[label]" class="checkin-photo-indicator">
                  <CIcon name="cilCheckCircle" class="checkin-check-icon"/>
                  <span class="checkin-photo-text">
                    {{ checkOutPhotoPreviews[label] ? 'Foto agregada' : 'Video agregado' }}
                  </span>
                </div>
                <div v-else class="text-danger mt-1" style="font-size: 0.875rem;">
                  Foto o video requerido
                </div>
              </div>
              
              <!-- Vista previa de foto/video o botón para agregar -->
              <div class="checkin-card-right">
                <!-- Vista previa de foto capturada -->
                <div v-if="checkOutPhotoPreviews[label]" class="checkin-photo-thumbnail">
                  <img 
                    :src="checkOutPhotoPreviews[label]" 
                    alt="Vista previa"
                    class="checkin-thumbnail-img"
                  />
                  <div class="checkin-thumbnail-overlay">
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeCheckOutMedia(label)"
                      class="checkin-thumbnail-btn"
                      title="Eliminar foto"
                    >
                      <CIcon name="cilTrash" class="text-white"/>
                    </b-button>
                  </div>
                </div>
                
                <!-- Vista previa de video capturado -->
                <div v-else-if="checkOutVideoPreviews[label]" class="checkin-photo-thumbnail">
                  <video 
                    :src="checkOutVideoPreviews[label]" 
                    class="checkin-thumbnail-img"
                    controls
                  ></video>
                  <div class="checkin-thumbnail-overlay">
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeCheckOutMedia(label)"
                      class="checkin-thumbnail-btn"
                      title="Eliminar video"
                    >
                      <CIcon name="cilTrash" class="text-white"/>
                    </b-button>
                  </div>
                </div>
                
                <!-- Botones para agregar foto o video -->
                <div v-else class="checkin-add-photo">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="openCheckOutCamera(label, 'photo')"
                    class="checkin-add-photo-btn mr-1"
                    :disabled="isCapturingCheckOutPhoto || isRecordingCheckOutVideo || activeCheckOutCameraOption === label"
                  >
                    <CIcon name="cilCamera" class="mr-1"/> 
                    <span v-if="activeCheckOutCameraOption === label">Abriendo...</span>
                    <span v-else>Foto</span>
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="openCheckOutCamera(label, 'video')"
                    class="checkin-add-photo-btn"
                    :disabled="isCapturingCheckOutPhoto || isRecordingCheckOutVideo || activeCheckOutCameraOption === label"
                  >
                    <CIcon name="cilVideo" class="mr-1"/> 
                    <span v-if="activeCheckOutCameraOption === label">Abriendo...</span>
                    <span v-else>Video</span>
                  </b-button>
                </div>
              </div>
            </div>
            
            <!-- Vista previa de la cámara cuando está abierta -->
            <div v-if="activeCheckOutCameraOption === label && !checkOutPhotoPreviews[label] && !checkOutVideoPreviews[label]" class="checkin-camera-preview-container mt-3">
              <video
                :ref="`checkout-camera-${label}`"
                autoplay
                playsinline
                muted
                class="checkin-camera-preview"
              ></video>
              <div class="checkin-camera-controls mt-2">
                <template v-if="activeCheckOutCameraMode === 'photo'">
                  <b-button
                    variant="success"
                    size="sm"
                    @click="captureCheckOutPhoto(label)"
                    class="mr-2"
                  >
                    <CIcon name="cilCheck" class="mr-1"/> Capturar Foto
                  </b-button>
                </template>
                <template v-else-if="activeCheckOutCameraMode === 'video'">
                  <b-button
                    v-if="!isRecordingCheckOutVideo"
                    variant="danger"
                    size="sm"
                    @click="startRecordingCheckOutVideo(label)"
                    class="mr-2"
                  >
                    <CIcon name="cilMediaRecord" class="mr-1"/> Iniciar Grabación
                  </b-button>
                  <b-button
                    v-else
                    variant="danger"
                    size="sm"
                    @click="stopRecordingCheckOutVideo(label)"
                    class="mr-2"
                  >
                    <CIcon name="cilMediaStop" class="mr-1"/> Detener
                  </b-button>
                </template>
                <b-button
                  variant="secondary"
                  size="sm"
                  @click="closeCheckOutCamera(label)"
                >
                  <CIcon name="cilXCircle" class="mr-1"/> Cancelar
                </b-button>
              </div>
            </div>
          </div>
          
          <!-- Item adicional editable para comentario -->
          <div class="checkin-card checkin-card-selected">
            <div class="checkin-card-content">
              <div class="checkin-card-left">
                <b-form-input
                  id="checkout-comment"
                  v-model="checkOutComment"
                  placeholder="Escribe un comentario adicional (opcional)..."
                  class="checkin-custom-input mb-2"
                  size="sm"
                ></b-form-input>
                
                <!-- Indicador de foto/video agregada -->
                <div v-if="checkOutPhotoPreviews['custom-comment'] || checkOutVideoPreviews['custom-comment']" class="checkin-photo-indicator">
                  <CIcon name="cilCheckCircle" class="checkin-check-icon"/>
                  <span class="checkin-photo-text">
                    {{ checkOutPhotoPreviews['custom-comment'] ? 'Foto agregada' : 'Video agregado' }}
                  </span>
                </div>
              </div>
              
              <!-- Vista previa de foto/video o botón para agregar -->
              <div class="checkin-card-right">
                <!-- Vista previa de foto capturada -->
                <div v-if="checkOutPhotoPreviews['custom-comment']" class="checkin-photo-thumbnail">
                  <img 
                    :src="checkOutPhotoPreviews['custom-comment']" 
                    alt="Vista previa"
                    class="checkin-thumbnail-img"
                  />
                  <div class="checkin-thumbnail-overlay">
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeCheckOutMedia('custom-comment')"
                      class="checkin-thumbnail-btn"
                      title="Eliminar foto"
                    >
                      <CIcon name="cilTrash" class="text-white"/>
                    </b-button>
                  </div>
                </div>
                
                <!-- Vista previa de video capturado -->
                <div v-else-if="checkOutVideoPreviews['custom-comment']" class="checkin-photo-thumbnail">
                  <video 
                    :src="checkOutVideoPreviews['custom-comment']" 
                    class="checkin-thumbnail-img"
                    controls
                  ></video>
                  <div class="checkin-thumbnail-overlay">
                    <b-button
                      variant="link"
                      size="sm"
                      @click="removeCheckOutMedia('custom-comment')"
                      class="checkin-thumbnail-btn"
                      title="Eliminar video"
                    >
                      <CIcon name="cilTrash" class="text-white"/>
                    </b-button>
                  </div>
                </div>
                
                <!-- Botones para agregar foto o video -->
                <div v-else class="checkin-add-photo">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="openCheckOutCamera('custom-comment', 'photo')"
                    class="checkin-add-photo-btn mr-1"
                    :disabled="isCapturingCheckOutPhoto || isRecordingCheckOutVideo || activeCheckOutCameraOption === 'custom-comment'"
                  >
                    <CIcon name="cilCamera" class="mr-1"/> 
                    <span v-if="activeCheckOutCameraOption === 'custom-comment'">Abriendo...</span>
                    <span v-else>Foto</span>
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    @click="openCheckOutCamera('custom-comment', 'video')"
                    class="checkin-add-photo-btn"
                    :disabled="isCapturingCheckOutPhoto || isRecordingCheckOutVideo || activeCheckOutCameraOption === 'custom-comment'"
                  >
                    <CIcon name="cilVideo" class="mr-1"/> 
                    <span v-if="activeCheckOutCameraOption === 'custom-comment'">Abriendo...</span>
                    <span v-else>Video</span>
                  </b-button>
                </div>
              </div>
            </div>
            
            <!-- Vista previa de la cámara cuando está abierta -->
            <div v-if="activeCheckOutCameraOption === 'custom-comment' && !checkOutPhotoPreviews['custom-comment'] && !checkOutVideoPreviews['custom-comment']" class="checkin-camera-preview-container mt-3">
              <video
                :ref="`checkout-camera-custom-comment`"
                autoplay
                playsinline
                muted
                class="checkin-camera-preview"
              ></video>
              <div class="checkin-camera-controls mt-2">
                <template v-if="activeCheckOutCameraMode === 'photo'">
                  <b-button
                    variant="success"
                    size="sm"
                    @click="captureCheckOutPhoto('custom-comment')"
                    class="mr-2"
                  >
                    <CIcon name="cilCheck" class="mr-1"/> Capturar Foto
                  </b-button>
                </template>
                <template v-else-if="activeCheckOutCameraMode === 'video'">
                  <b-button
                    v-if="!isRecordingCheckOutVideo"
                    variant="danger"
                    size="sm"
                    @click="startRecordingCheckOutVideo('custom-comment')"
                    class="mr-2"
                  >
                    <CIcon name="cilMediaRecord" class="mr-1"/> Iniciar Grabación
                  </b-button>
                  <b-button
                    v-else
                    variant="danger"
                    size="sm"
                    @click="stopRecordingCheckOutVideo('custom-comment')"
                    class="mr-2"
                  >
                    <CIcon name="cilMediaStop" class="mr-1"/> Detener
                  </b-button>
                </template>
                <b-button
                  variant="secondary"
                  size="sm"
                  @click="closeCheckOutCamera('custom-comment')"
                >
                  <CIcon name="cilXCircle" class="mr-1"/> Cancelar
                </b-button>
              </div>
            </div>
          </div>
        </div>
        
        <b-alert v-if="checkOutError" variant="danger" dismissible @dismissed="checkOutError = ''">
          {{ checkOutError }}
        </b-alert>
      </div>
      
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="isUploadingCheckOut">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok()" 
          :disabled="!hasAllCheckOutMedia || isUploadingCheckOut"
          class="checkin-continue-btn"
        >
          <CIcon name="cilCheck" class="mr-1"/>
          <span v-if="isUploadingCheckOut">
            <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Guardando...
          </span>
          <span v-else>Continuar</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Modal de Detalle de Check-In -->
    <b-modal
      v-model="showCheckInDetailModal"
      title="Detalle de Check-In"
      size="lg"
      hide-footer
      header-class="checkin-modal-header"
      body-class="checkin-modal-body"
    >
      <div v-if="checkInEvidence" class="checkin-detail">
        <p class="text-muted mb-3">
          <strong>Fecha:</strong> {{ formatDate(checkInEvidence.created_at) }}
        </p>
        
        <div class="checkin-detail-items">
          <div 
            v-for="(item, index) in checkInEvidenceItems" 
            :key="index"
            class="checkin-detail-item mb-3"
          >
            <div class="checkin-detail-card">
              <div class="checkin-detail-content">
                <div class="checkin-detail-left">
                  <strong class="checkin-detail-label">{{ item.label || 'Sin etiqueta' }}</strong>
                </div>
                <div class="checkin-detail-right">
                  <div v-if="item.photo" class="checkin-detail-photo-thumbnail" @click="openImageFullscreen(item.photo, item.label)">
                    <img 
                      :src="item.photo" 
                      alt="Foto"
                      class="checkin-thumbnail-img"
                    />
                    <div class="checkin-thumbnail-overlay">
                      <CIcon name="cilZoom" class="text-white"/>
                    </div>
                  </div>
                  <div v-else-if="item.video" class="checkin-detail-photo-thumbnail" @click="openVideoFullscreen(item.video, item.label)">
                    <video 
                      :src="item.video" 
                      class="checkin-thumbnail-img"
                      muted
                    ></video>
                    <div class="checkin-thumbnail-overlay">
                      <CIcon name="cilMediaPlay" class="text-white"/>
                    </div>
                  </div>
                  <div v-else class="checkin-detail-no-photo">
                    <CIcon name="cilImage" class="text-muted"/>
                    <small class="text-muted d-block">Sin media</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <p class="text-muted">No hay información de check-in disponible</p>
      </div>
    </b-modal>

    <!-- Modal de Detalle de Check-Out -->
    <b-modal
      v-model="showCheckOutDetailModal"
      title="Detalle de Check-Out"
      size="lg"
      hide-footer
      header-class="checkin-modal-header"
      body-class="checkin-modal-body"
    >
      <div v-if="checkOutEvidence" class="checkin-detail">
        <p class="text-muted mb-3">
          <strong>Fecha:</strong> {{ formatDate(checkOutEvidence.created_at) }}
        </p>
        
        <div class="checkin-detail-items">
          <div 
            v-for="(item, index) in checkOutEvidenceItems" 
            :key="index"
            class="checkin-detail-item mb-3"
          >
            <div class="checkin-detail-card">
              <div class="checkin-detail-content">
                <div class="checkin-detail-left">
                  <strong class="checkin-detail-label">{{ item.label || 'Sin etiqueta' }}</strong>
                </div>
                <div class="checkin-detail-right">
                  <div v-if="item.photo" class="checkin-detail-photo-thumbnail" @click="openImageFullscreen(item.photo, item.label)">
                    <img 
                      :src="item.photo" 
                      alt="Foto"
                      class="checkin-thumbnail-img"
                    />
                    <div class="checkin-thumbnail-overlay">
                      <CIcon name="cilZoom" class="text-white"/>
                    </div>
                  </div>
                  <div v-else-if="item.video" class="checkin-detail-photo-thumbnail" @click="openVideoFullscreen(item.video, item.label)">
                    <video 
                      :src="item.video" 
                      class="checkin-thumbnail-img"
                      muted
                    ></video>
                    <div class="checkin-thumbnail-overlay">
                      <CIcon name="cilMediaPlay" class="text-white"/>
                    </div>
                  </div>
                  <div v-else class="checkin-detail-no-photo">
                    <CIcon name="cilImage" class="text-muted"/>
                    <small class="text-muted d-block">Sin media</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4">
        <p class="text-muted">No hay información de check-out disponible</p>
      </div>
    </b-modal>

    <!-- Modal Fullscreen para Imágenes y Videos -->
    <b-modal
      v-model="showFullscreenImage"
      size="xl"
      centered
      hide-header
      hide-footer
      @hide="closeFullscreenImage"
      class="fullscreen-image-modal"
    >
      <div class="fullscreen-image-container">
        <button 
          class="fullscreen-close-btn"
          @click="closeFullscreenImage"
        >
          <CIcon name="cilXCircle" class="text-white"/>
        </button>
        <img 
          v-if="fullscreenMediaType === 'image'"
          :src="fullscreenImageUrl" 
          alt="Imagen en pantalla completa"
          class="fullscreen-image"
        />
        <video 
          v-else-if="fullscreenMediaType === 'video'"
          :src="fullscreenVideoUrl" 
          controls
          class="fullscreen-image"
        ></video>
        <div v-if="fullscreenImageLabel" class="fullscreen-image-label">
          <strong>{{ fullscreenImageLabel }}</strong>
        </div>
      </div>
    </b-modal>

    <!-- Botón flotante estilo Android con speed dial -->
    <div v-if="service" class="fab-container">
      <!-- Overlay para cerrar al hacer clic fuera -->
      <div 
        v-if="fabMenuOpen" 
        class="fab-overlay"
        @click="fabMenuOpen = false">
      </div>
      
      <!-- Botones secundarios -->
      <transition-group name="fab-item" tag="div" class="fab-items">
        <div 
          v-if="fabMenuOpen && shouldShowEvidence"
          key="evidencia"
          class="fab-item"
          :style="{ bottom: getEvidenceButtonBottom(), zIndex: 1002 }"
          @click="handleFabAction('evidencia')">
          <div class="fab-label">Evidencia</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#9c27b0', zIndex: 1002 }"
            :disabled="loading">
            <CIcon name="cilImage" width="20" style="color: #fff;"/>
          </button>
        </div>
        <div 
          v-if="fabMenuOpen && shouldShowCheckIn"
          key="checkin"
          class="fab-item"
          :style="{ bottom: getCheckInButtonBottom(), zIndex: 1002 }"
          @click="handleFabAction('checkin')">
          <div class="fab-label">Check In</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#17a2b8', zIndex: 1002 }"
            :disabled="loading">
            <CIcon name="cilCheckCircle" width="20" style="color: #fff;"/>
          </button>
        </div>
        <div 
          v-if="fabMenuOpen && shouldShowCheckOut"
          key="checkout"
          class="fab-item"
          :style="{ bottom: getCheckOutButtonBottom(), zIndex: 1002 }"
          @click="handleFabAction('checkout')">
          <div class="fab-label">Check Out</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#ff9800', zIndex: 1002 }"
            :disabled="loading">
            <CIcon name="cilArrowRight" width="20" style="color: #fff;"/>
          </button>
        </div>
        <div 
          v-if="fabMenuOpen && service.estado_id === 2 && userProfile"
          key="finalizar"
          class="fab-item"
          :style="{ bottom: getFinalizarButtonBottom() }"
          @click="handleFabAction('finalizar')">
          <div class="fab-label">Finalizar</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#bf0811' }"
            :disabled="loading || !canFinalizeService">
            <CIcon name="cilMediaStop" width="20" style="color: #fff;"/>
          </button>
        </div>
        <div 
          v-if="fabMenuOpen && service.estado_id === 1 && userProfile"
          key="iniciar"
          class="fab-item"
          :style="{ bottom: getIniciarButtonBottom() }"
          @click="handleFabAction('iniciar')">
          <div class="fab-label">Iniciar</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#336535' }"
            :disabled="loading">
            <CIcon name="cilCheck" width="20" style="color: #fff;"/>
          </button>
        </div>
        <div 
          v-if="fabMenuOpen"
          key="refrescar"
          class="fab-item"
          :style="{ bottom: getRefreshButtonBottom() }"
          @click="handleFabAction('refrescar')">
          <div class="fab-label">Refrescar</div>
          <button 
            class="fab-button secondary"
            :style="{ backgroundColor: '#6c757d' }"
            :disabled="loading">
            <CIcon name="cilReload" width="20" style="color: #fff;"/>
          </button>
        </div>
      </transition-group>
      
      <!-- Botón principal -->
      <button 
        class="fab-button primary"
        :style="{ backgroundColor: service.estado_color, transform: fabMenuOpen ? 'rotate(45deg)' : 'rotate(0deg)' }"
        @click="toggleFabMenu"
        :disabled="loading">
        <CIcon name="cilOptions" width="24" style="color: #fff;"/>
      </button>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import moment from 'moment';

const TYPES = {
  CHECK_IN: "check_in",
  CHECK_OUT: "check_out",
  POSITION: "position",
  EVIDENCE: "evidence",
}

export default {
  name: 'ServiceDetail',
  components: {
    Loading
  },
  data() {
    return {
      loading: false,
      userProfile: null,
      service: null,
      serviceIndex: 1,
      fabMenuOpen: false,
      showCheckInModal: false,
      checkInOptions: [
        { value: 'mascota', label: 'Mascota' },
        { value: 'comida', label: 'Comida' },
        { value: 'platos', label: 'Platos' },
        { value: 'cama', label: 'Cama' },
        { value: 'collar', label: 'Collar' },
        { value: 'identificacion', label: 'Identificación' },
        { value: 'juguete', label: 'Juguete' },
        { value: 'video', label: 'Video' },
        { value: 'otro', label: 'Otro' }
      ],
      selectedCheckInItems: [],
      checkInPhotos: {},
      checkInPhotoPreviews: {},
      checkInVideos: {},
      checkInVideoPreviews: {},
      checkInOtherText: '',
      isUploadingCheckIn: false,
      checkInError: '',
      activeCameraOption: null,
      activeCameraStream: null,
      activeCameraMode: null, // 'photo' o 'video'
      isCapturingPhoto: false,
      isRecordingVideo: false,
      activeMediaRecorder: null,
      // Evidencia
      showEvidenceModal: false,
      evidenceComment: '',
      evidencePhoto: null,
      evidencePhotoPreview: null,
      evidenceVideo: null,
      evidenceVideoPreview: null,
      activeEvidenceCamera: false,
      activeEvidenceCameraStream: null,
      activeEvidenceCameraMode: null, // 'photo' o 'video'
      isCapturingEvidencePhoto: false,
      isRecordingEvidenceVideo: false,
      activeEvidenceMediaRecorder: null,
      isUploadingEvidence: false,
      evidenceError: '',
      showCheckInDetailModal: false,
      showCheckOutDetailModal: false,
      fullscreenImageUrl: null,
      fullscreenImageLabel: null,
      fullscreenVideoUrl: null,
      showFullscreenImage: false,
      fullscreenMediaType: null, // 'image' o 'video'
      // GPS
      gpsPosition: null,
      isSendingGPS: false,
      isGettingGPS: false,
      // Timer cada 4 min para paseo (solo estado 2): envío automático de ubicación
      gpsPeriodicCountdownSeconds: 0,
      gpsPeriodicCountdownTimerId: null,
      // Checkout
      showCheckOutModal: false,
      checkOutPhotos: {},
      checkOutPhotoPreviews: {},
      checkOutVideos: {},
      checkOutVideoPreviews: {},
      isUploadingCheckOut: false,
      checkOutError: '',
      checkOutComment: '',
      activeCheckOutCameraOption: null,
      activeCheckOutCameraStream: null,
      activeCheckOutCameraMode: null, // 'photo' o 'video'
      isCapturingCheckOutPhoto: false,
      isRecordingCheckOutVideo: false,
      activeCheckOutMediaRecorder: null
    }
  },
  computed: {
    shouldShowCheckIn() {
      const result = this.service && 
             this.service.estado_id === 2 
             && this.service.check_in_done === false
             && this.service.servicio_detalle
             && this.service.servicio_detalle.toLowerCase().includes('guardería')
             && this.userProfile;
      return result;
    },
    shouldShowEvidence() {
      if (!this.service || !this.userProfile || this.service.estado_id !== 2) {
        return false;
      }
      
      const isGuarderia = this.service.servicio_detalle && 
                          this.service.servicio_detalle.toLowerCase().includes('guardería');
      
      // Si es guardería, debe tener check_in_done = true
      // Si no es guardería, siempre se muestra
      const result = isGuarderia 
        ? (this.service.check_in_done === true)
        : true;
      
      return result;
    },
    hasSelectedItems() {
      // Debe haber al menos un item seleccionado y "mascota" es obligatorio
      if (this.selectedCheckInItems.length === 0 || !this.selectedCheckInItems.includes('mascota')) {
        return false;
      }
      
      // "mascota" también debe tener foto o video
      const hasMascotaPhoto = !!this.checkInPhotoPreviews['mascota'];
      const hasMascotaVideo = !!this.checkInVideoPreviews['mascota'];
      
      return hasMascotaPhoto || hasMascotaVideo;
    },
    checkInEvidence() {
      if (!this.service || !this.service.evidencias) {
        return null;
      }
      return this.service.evidencias.find(ev => ev.check_in === true) || null;
    },
    checkInEvidenceItems() {
      if (!this.checkInEvidence || !this.checkInEvidence.evidence) {
        return [];
      }
      return this.checkInEvidence.evidence.filter(item => item.label || item.photo);
    },
    checkOutEvidence() {
      if (!this.service || !this.service.evidencias) {
        return null;
      }
      return this.service.evidencias.find(ev => ev.type === TYPES.CHECK_OUT) || null;
    },
    checkOutEvidenceItems() {
      if (!this.checkOutEvidence || !this.checkOutEvidence.evidence) {
        return [];
      }
      return this.checkOutEvidence.evidence.filter(item => item.label || item.photo || item.video);
    },
    nonCheckInEvidences() {
      if (!this.service || !this.service.evidencias) {
        return [];
      }
      return this.service.evidencias
        .filter(ev => ev.check_in === false && ev.type !== TYPES.POSITION)
        .sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateA - dateB; // Ascendente
        });
    },
    shouldShowCheckOut() {
      if (!this.service || !this.userProfile || this.service.estado_id !== 2) {
        return false;
      }
      
      // Debe tener check_in_done = true
      if (!this.service.check_in_done) {
        return false;
      }
      
      // Debe tener last_service = true
      if (!this.service.last_service) {
        return false;
      }
      
      // No debe existir ya un checkout realizado
      if (this.hasCheckOut) {
        return false;
      }
      
      // Debe existir una evidencia de tipo check_in
      const hasCheckIn = this.service.evidencias && 
                         this.service.evidencias.some(ev => ev.type === TYPES.CHECK_IN);
      
      if (!hasCheckIn) {
        return false;
      }
      
      const hasEvidence = this.service.evidencias && 
                          this.service.evidencias.some(ev => ev.type === TYPES.EVIDENCE);
      
      return hasEvidence;
    },
    hasCheckOut() {
      if (!this.service || !this.service.evidencias) {
        return false;
      }
      return this.service.evidencias.some(ev => ev.type === TYPES.CHECK_OUT);
    },
    checkOutItems() {
      // Obtener los labels del check_in existente
      if (!this.checkInEvidence || !this.checkInEvidence.evidence) {
        return [];
      }
      return this.checkInEvidence.evidence
        .filter(item => item.label)
        .map(item => item.label);
    },
    hasAllCheckOutMedia() {
      // Validar que todos los items del checkout tengan foto o video
      if (this.checkOutItems.length === 0) {
        return false;
      }
      
      return this.checkOutItems.every(label => {
        const hasPhoto = !!this.checkOutPhotoPreviews[label];
        const hasVideo = !!this.checkOutVideoPreviews[label];
        return hasPhoto || hasVideo;
      });
    },
    canFinalizeService() {
      if (!this.service) return false;
      // Paseo/adiestramiento: flujo Programado → Iniciar → Gestión evidencia → Finalizar (sin check-in/check-out)
      if (this.isPaseoService) {
        return true;
      }
      // Guardería/pasadía: si last_service = true, debe tener checkout antes de finalizar
      if (this.service.last_service === true) {
        return this.hasCheckOut;
      }
      return true;
    },
    // Paseo o adiestramiento (no guardería ni pasadía); usado para no exigir checkout y para el timer GPS
    isPaseoService() {
      if (!this.service || this.service.estado_id !== 2) return false;
      const detalle = (this.service.servicio_detalle || '').toLowerCase();
      return !detalle.includes('guardería') && !detalle.includes('pasadía');
    },
    // Tipo paseo/adiestramiento (cualquier estado); usado para mostrar el mapa en estado 2 y 34
    isPaseoTipo() {
      if (!this.service) return false;
      const detalle = (this.service.servicio_detalle || '').toLowerCase();
      return !detalle.includes('guardería') && !detalle.includes('pasadía');
    },
    // Mostrar mapa: programado (1) para ver ubicación cliente, en curso (2), o finalizado (34) como evidencia
    showRecorridoMap() {
      return this.service && this.isPaseoTipo && (this.service.estado_id === 1 || this.service.estado_id === 2 || this.service.estado_id === 34);
    },
    // Solo en estado 2 se ejecuta el timer de 4 min y se puede enviar posición
    isRecorridoSoloVista() {
      return this.service && this.isPaseoTipo && this.service.estado_id === 34;
    },
    // Estado programado: mapa solo para ver ubicación del cliente
    isRecorridoProgramado() {
      return this.service && this.isPaseoTipo && this.service.estado_id === 1;
    },
    recorridoPath() {
      if (!this.service || !this.service.evidencias) return [];
      const positionEvidences = this.service.evidencias
        .filter(ev => ev.type === TYPES.POSITION)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const path = [];
      positionEvidences.forEach(ev => {
        if (ev.evidence && Array.isArray(ev.evidence)) {
          ev.evidence.forEach(p => {
            if (p != null && (p.lat != null && p.lat !== '') && (p.long != null && p.long !== '' || p.lng != null && p.lng !== '')) {
              path.push({
                lat: Number(p.lat),
                lng: Number(p.long != null && p.long !== '' ? p.long : p.lng)
              });
            }
          });
        }
      });
      return path;
    },
    recorridoMapCenter() {
      if (this.recorridoPath.length > 0) {
        return this.recorridoPath[Math.floor(this.recorridoPath.length / 2)];
      }
      if (this.service && this.service.pedido && this.service.pedido.cliente && this.service.pedido.cliente.ubicacion) {
        const u = this.service.pedido.cliente.ubicacion;
        return { lat: Number(u.lat), lng: Number(u.lng) };
      }
      if (this.gpsPosition) {
        return { lat: this.gpsPosition.lat, lng: this.gpsPosition.long };
      }
      return { lat: 4.6, lng: -74.08 };
    },
    recorridoHomePosition() {
      if (!this.service || !this.service.pedido || !this.service.pedido.cliente || !this.service.pedido.cliente.ubicacion) return null;
      const u = this.service.pedido.cliente.ubicacion;
      return { lat: Number(u.lat), lng: Number(u.lng) };
    },
    recorridoLastPosition() {
      if (this.recorridoPath.length === 0) return null;
      return this.recorridoPath[this.recorridoPath.length - 1];
    },
    // Efecto salto (bounce) en el icono de huellas solo cuando paseo está en ejecución (estado 2)
    recorridoPawMarkerAnimation() {
      return this.service && this.service.estado_id === 2 && this.isPaseoTipo ? 1 : 0;
    },
    // Bitácora: WFStates + evidencias (check-in, evidence) unidos y ordenados por fecha/hora
    bitacoraItems() {
      if (!this.service) return [];
      const events = [];

      if (this.service.WFStates && Array.isArray(this.service.WFStates)) {
        this.service.WFStates.forEach(state => {
          const date = state.dateTime ? new Date(state.dateTime) : null;
          if (date && !isNaN(date.getTime())) {
            events.push({
              date,
              type: 'wfstate',
              stateId: state.stateId,
              stateName: state.stateName,
              stateColor: state.stateColor
            });
          }
        });
      }

      if (this.service.evidencias) {
        const checkInEvidence = this.service.evidencias.find(ev => ev.check_in === true || ev.type === 'check_in');
        if (checkInEvidence) {
          const date = this.parseServiceDate(checkInEvidence.created_at);
          if (date) {
            events.push({
              date,
              type: 'checkin',
              time: this.formatTimeForBitacora(checkInEvidence.created_at)
            });
          }
        }
        this.service.evidencias
          .filter(ev => ev.type === TYPES.EVIDENCE)
          .forEach(ev => {
            if (ev.evidence && ev.evidence[0]) {
              const date = this.parseServiceDate(ev.created_at);
              if (date) {
                events.push({ date, type: 'evidence', ev });
              }
            }
          });
      }

      events.sort((a, b) => a.date.getTime() - b.date.getTime());
      const items = [];
      events.forEach(event => {
        if (event.type === 'wfstate') {
          const time = moment(event.date).format('HH:mm');
          let icon = 'cilMediaPlay';
          let iconClass = 'bitacora-icon-success';
          let emoji = '🚶';
          if (event.stateId === 34) {
            icon = 'cilMediaStop';
            iconClass = 'bitacora-icon-danger';
            emoji = '🏁';
          } else if (event.stateId === 134) {
            icon = 'cilCloud';
            iconClass = 'bitacora-icon-info';
            emoji = '🌧️';
          }
          items.push({
            time,
            icon,
            iconClass,
            label: event.stateName,
            emoji,
            isWfState: true
          });
        } else if (event.type === 'checkin') {
          items.push({
            time: moment(event.date).format('HH:mm'),
            icon: 'cilCheck',
            iconClass: 'bitacora-icon-success',
            label: 'Check-in realizado',
            emoji: '✅',
            isCheckIn: true
          });
        } else if (event.type === 'evidence') {
          const evidence = event.ev.evidence[0];
          const time = moment(event.date).format('HH:mm');
          let icon = 'cilClock';
          let iconClass = 'bitacora-icon-info';
          let emoji = '';
          if (evidence.label && typeof evidence.label.toLowerCase === 'function') {
            if (evidence.label.toLowerCase().includes('comida')) emoji = '🍽️';
            else if (evidence.label.toLowerCase().includes('juego') || evidence.label.toLowerCase().includes('jardín')) {
              iconClass = 'bitacora-icon-warning';
              emoji = '🎾';
            }
          }
          const hasVideo = !!evidence.video;
          const hasPhoto = !!evidence.photo && !hasVideo;
          items.push({
            time,
            icon,
            iconClass,
            label: evidence.label || 'Evidencia',
            emoji,
            videoLink: hasVideo,
            videoUrl: evidence.video || null,
            hasPhoto,
            photoUrl: evidence.photo
          });
        }
      });
      return items;
    }
  },
  watch: {
    // Timer solo cuando el paseo está en curso (estado 2); al finalizar (34) se suspende
    isPaseoService(val) {
      if (val) {
        this.startGpsPeriodicTimer();
      } else {
        this.stopGpsPeriodicTimer();
      }
    }
  },
  created() {
    let userProfile = JSON.parse(this.$store.getters.userProfile);
    if (userProfile) {
      this.userProfile = userProfile;
    }
    this.loadServiceData();
    this.initGPS();
  },
  mounted() {},
  beforeDestroy() {
    this.stopGpsPeriodicTimer();
  },
  methods: {
    // Métodos de GPS - Modo a demanda (bajo consumo de batería)
    async initGPS() {
      // No iniciar watchPosition automáticamente
      // El GPS se obtendrá solo cuando el usuario lo solicite
      console.log('GPS inicializado en modo a demanda');
    },
    // Timer cada 4 minutos para paseo (solo en ServiceDetail, estado 2): envía ubicación automáticamente
    startGpsPeriodicTimer() {
      this.stopGpsPeriodicTimer();
      // const FOUR_MINUTES_SECONDS = 4 * 60;
      const FOUR_MINUTES_SECONDS = 30; //TODO: counter
      this.gpsPeriodicCountdownSeconds = FOUR_MINUTES_SECONDS;
      this.gpsPeriodicCountdownTimerId = setInterval(() => {
        this.gpsPeriodicCountdownSeconds--;
        if (this.gpsPeriodicCountdownSeconds <= 0) {
          this.sendPeriodicGpsPosition();
          this.gpsPeriodicCountdownSeconds = FOUR_MINUTES_SECONDS;
        }
      }, 1000);
    },
    stopGpsPeriodicTimer() {
      if (this.gpsPeriodicCountdownTimerId) {
        clearInterval(this.gpsPeriodicCountdownTimerId);
        this.gpsPeriodicCountdownTimerId = null;
      }
      this.gpsPeriodicCountdownSeconds = 0;
    },
    async sendPeriodicGpsPosition() {
      if (!this.service || this.service.estado_id !== 2) return;
      try {
        const ok = await this.sendGPSPosition('position');
        if (ok) {
          this.$bvToast.toast('Ubicación enviada automáticamente', {
            title: 'GPS',
            variant: 'success',
            solid: true,
            autoHideDelay: 2000
          });
          this.refreshService();
        }
      } catch (e) {
        console.error('Error envío periódico GPS:', e);
        this.$bvToast.toast('No se pudo enviar la ubicación. Revisa el GPS.', {
          title: 'GPS',
          variant: 'warning',
          solid: true
        });
      }
    },
    formatGpsCountdown(seconds) {
      if (seconds <= 0) return '0:00';
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    async getGPSOnDemand() {
      // Obtener GPS solo cuando se solicite (a demanda)
      this.isGettingGPS = true;
      
      try {
        const position = await this.getCurrentGPSPosition();
        
        this.$bvToast.toast('Coordenadas GPS obtenidas correctamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        });
      } catch (error) {
        console.error('Error al obtener GPS:', error);
        
        let errorMessage = 'No se pudo obtener la ubicación GPS.';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = 'Permisos de ubicación denegados. Por favor, habilita los permisos en la configuración del navegador.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = 'La información de ubicación no está disponible.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = 'Tiempo de espera agotado al obtener la ubicación.';
        }
        
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        
        this.gpsPosition = null;
      } finally {
        this.isGettingGPS = false;
      }
    },
    async getCurrentGPSPosition() {
      // Obtener posición actual usando getCurrentPosition (a demanda)
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocalización no soportada por este navegador'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const gpsPos = {
              lat: position.coords.latitude,
              long: position.coords.longitude
            };
            this.gpsPosition = gpsPos;
            resolve(gpsPos);
          },
          (error) => {
            console.error('Error al obtener posición GPS:', error);
            reject(error);
          },
          {
            enableHighAccuracy: true, // GPS preciso cuando se necesita
            timeout: 10000,
            maximumAge: 0 // Siempre obtener posición fresca
          }
        );
      });
    },
    async sendGPSPosition(actionType) {
      try {
        // Obtener posición actual a demanda antes de enviar
        const position = await this.getCurrentGPSPosition();
        
        // Llamar a createEvidenciaPosition
        const createEvidenciaPosition = this.$firebase.functions().httpsCallable('createEvidencePosition');
        const payload = {
          serviceId: this.service.id,
          lat: position.lat,
          long: position.long,
          type: TYPES.POSITION,
        };

        const { data: response } = await createEvidenciaPosition(payload);
        return true;
      } catch (error) {
        console.error(`Error al enviar posición GPS para ${actionType}:`, error);
        return false;
      }
    },
    async sendGPSManually() {
      if (!this.gpsPosition) {
        this.$bvToast.toast('Primero debes obtener las coordenadas GPS', {
          title: 'Sin coordenadas',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.isSendingGPS = true;
      try {
        const success = await this.sendGPSPosition('manual');
        if (success) {
          this.$bvToast.toast('Posición GPS enviada correctamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true
          });
          // Refrescar servicio para obtener distancia actualizada (backend la calcula al recibir position)
          this.refreshService();
        } else {
          throw new Error('No se pudo enviar la posición');
        }
      } catch (error) {
        console.error('Error al enviar GPS manualmente:', error);
        this.$bvToast.toast('Error al enviar la posición GPS. Por favor, intenta nuevamente.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isSendingGPS = false;
      }
    },
    loadServiceData() {
      this.loading = true;
      try {
        if (this.$route.query.serviceData) {
          const decodedData = decodeURIComponent(this.$route.query.serviceData);
          this.service = JSON.parse(decodedData);
          this.serviceIndex = parseInt(this.$route.query.index) || 1;
          this.refreshService();
        }
      } catch (error) {
        console.error('Error al cargar datos del servicio:', error);
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    toggleFabMenu() {
      this.fabMenuOpen = !this.fabMenuOpen;
    },
    handleFabAction(action) {
      this.fabMenuOpen = false;
      if (action === 'refrescar') {
        this.refreshService();
      } else if (action === 'iniciar' || action === 'finalizar') {
        this.initiateService();
      } else if (action === 'checkin') {
        this.showCheckInModal = true;
      } else if (action === 'checkout') {
        this.showCheckOutModal = true;
      } else if (action === 'evidencia') {
        this.showEvidenceModal = true;
      }
    },
    // Orden de botones (de abajo hacia arriba, más cerca del botón principal):
    // 1. Evidencia (si aplica) - más cerca (140px)
    // 2. CheckIn (si aplica)
    // 3. CheckOut (si aplica)
    // 4. Finalizar (si estado_id === 2)
    // 5. Iniciar (si estado_id === 1)
    // 6. Refrescar (siempre) - más lejos
    
    getEvidenceButtonBottom() {
      // Evidencia siempre está más cerca del botón principal
      return '140px';
    },
    getCheckInButtonBottom() {
      // CheckIn está después de Evidencia
      let bottom = 140;
      if (this.shouldShowEvidence) {
        bottom += 80;
      }
      return bottom + 'px';
    },
    getCheckOutButtonBottom() {
      // CheckOut está después de Evidencia y CheckIn
      let bottom = 140;
      if (this.shouldShowEvidence) {
        bottom += 80;
      }
      if (this.shouldShowCheckIn) {
        bottom += 80;
      }
      return bottom + 'px';
    },
    getFinalizarButtonBottom() {
      // Finalizar está después de Evidencia, CheckIn y CheckOut
      let bottom = 140;
      if (this.shouldShowEvidence) {
        bottom += 80;
      }
      if (this.shouldShowCheckIn) {
        bottom += 80;
      }
      if (this.shouldShowCheckOut) {
        bottom += 80;
      }
      return bottom + 'px';
    },
    getIniciarButtonBottom() {
      // Iniciar está después de Evidencia, CheckIn, CheckOut y Finalizar
      let bottom = 140;
      if (this.shouldShowEvidence) {
        bottom += 80;
      }
      if (this.shouldShowCheckIn) {
        bottom += 80;
      }
      if (this.shouldShowCheckOut) {
        bottom += 80;
      }
      if (this.service.estado_id === 2 && this.userProfile) {
        bottom += 80;
      }
      return bottom + 'px';
    },
    getRefreshButtonBottom() {
      // Refrescar siempre está más arriba (último)
      let bottom = 140;
      if (this.shouldShowEvidence) {
        bottom += 80;
      }
      if (this.shouldShowCheckIn) {
        bottom += 80;
      }
      if (this.shouldShowCheckOut) {
        bottom += 80;
      }
      if (this.service.estado_id === 2 && this.userProfile) {
        bottom += 80;
      }
      if (this.service.estado_id === 1 && this.userProfile) {
        bottom += 80;
      }
      return bottom + 'px';
    },
    async refreshService() {
      if (!this.service || !this.service.id) {
        return;
      }

      this.loading = true;
      try {
        const getServiceByIdV2 = this.$firebase.functions().httpsCallable('getServiceByIdWithEvidence');
        const payload = {
          serviceId: this.service.id
        };

        const { data: response } = await getServiceByIdV2(payload);
        
        // Actualizar this.service con la respuesta
        if (response) {
          // Si la respuesta tiene un campo 'result', usar ese, sino usar la respuesta directamente
          const serviceData = response.result || response;
          // Asegurar que se actualicen los campos evidencias y has_check_in
          this.service = { ...this.service, ...serviceData };
          // Forzar reactividad para los nuevos campos
          this.$forceUpdate();
          this.$bvToast.toast('Servicio refrescado correctamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true
          });
        }
      } catch (error) {
        console.error('Error al actualizar el servicio:', error);
        this.$bvToast.toast('Error al actualizar el servicio', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    async initiateService() {
      if (!this.userProfile || !this.service) {
        return;
      }

      // Activar loading al inicio para prevenir doble clic
      this.loading = true;

      try {
        // Obtener GPS a demanda antes de iniciar/finalizar
        if (!this.gpsPosition) {
          try {
            await this.getCurrentGPSPosition();
          } catch (error) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
              'Se requiere acceso a la ubicación GPS para continuar. ¿Deseas intentar nuevamente?',
              {
                title: 'Permisos de GPS requeridos',
                okVariant: 'primary',
                okTitle: 'Sí, intentar',
                cancelTitle: 'Cancelar',
                headerClass: 'p-2 border-bottom-0',
                footerClass: 'p-2 border-top-0',
                centered: true
              }
            );
            
            if (!confirmed) {
              this.loading = false;
              return;
            }
            
            // Intentar nuevamente
            try {
              await this.getCurrentGPSPosition();
            } catch (retryError) {
              this.$bvToast.toast('No se pudo obtener acceso a la ubicación GPS. Por favor, habilita los permisos de ubicación en la configuración de tu navegador.', {
                title: 'GPS no disponible',
                variant: 'warning',
                solid: true,
                autoHideDelay: 5000
              });
              this.loading = false;
              return;
            }
          }
        }

        // Enviar posición GPS (ya obtenida arriba)
        const actionType = this.service.estado_id === 1 ? 'iniciar' : 'finalizar';
        await this.sendGPSPosition(actionType);

        const updateServiceState = this.$firebase.functions().httpsCallable('updateServiceState');
        const payload = {
          walkerEmail: this.userProfile.email,
          serviceId: this.service.id,
          stateId: this.service.estado_id
        };

        const { data: response } = await updateServiceState(payload);
        
        if (response) {
          this.refreshService();
        }
      } catch (error) {
        console.error('Error al iniciar el servicio:', error);
        this.$bvToast.toast('Error al iniciar el servicio', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    onCheckInItemChange(value) {
      // Si se deselecciona un item, limpiar su foto/video
      if (!this.selectedCheckInItems.includes(value)) {
        // Crear nuevos objetos sin la propiedad para mantener reactividad
        const { [value]: removedPhoto, ...restPhotos } = this.checkInPhotos;
        this.checkInPhotos = restPhotos;
        const { [value]: removedPreview, ...restPreviews } = this.checkInPhotoPreviews;
        this.checkInPhotoPreviews = restPreviews;
        const { [value]: removedVideo, ...restVideos } = this.checkInVideos;
        this.checkInVideos = restVideos;
        const { [value]: removedVideoPreview, ...restVideoPreviews } = this.checkInVideoPreviews;
        this.checkInVideoPreviews = restVideoPreviews;
        if (value === 'otro') {
          this.checkInOtherText = '';
        }
      }
    },
    async openCamera(optionValue, mode = 'photo') {
      this.isCapturingPhoto = true;
      this.activeCameraOption = optionValue;
      this.activeCameraMode = mode;
      
      // Si se abre en modo video, limpiar vista previa anterior
      if (mode === 'video') {
        if (this.checkInVideoPreviews[optionValue]) {
          this.$set(this.checkInVideoPreviews, optionValue, null);
        }
        if (this.checkInVideos[optionValue]) {
          this.$set(this.checkInVideos, optionValue, null);
        }
      }
      
      try {
        // Solicitar acceso a la cámara y micrófono
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment' // Cámara trasera en móviles
          },
          audio: true // Incluir audio para videos
        });
        
        this.activeCameraStream = stream;
        
        // Mostrar el video en el elemento video
        this.$nextTick(() => {
          const videoRef = `camera-${optionValue}`;
          const video = this.$refs[videoRef];
          if (video) {
            const videoElement = Array.isArray(video) ? video[0] : video;
            if (videoElement) {
              videoElement.srcObject = stream;
              // Asegurar que esté silenciado durante la grabación
              videoElement.muted = true;
            }
          }
        });
      } catch (error) {
        console.error('Error al acceder a la cámara:', error);
        this.$bvToast.toast('Error al acceder a la cámara. Por favor, verifica los permisos.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        this.activeCameraOption = null;
        this.activeCameraStream = null;
        this.activeCameraMode = null;
      } finally {
        this.isCapturingPhoto = false;
      }
    },
    capturePhoto(optionValue) {
      const videoRef = `camera-${optionValue}`;
      const video = this.$refs[videoRef];
      
      if (!video) return;
      
      const videoElement = Array.isArray(video) ? video[0] : video;
      if (!videoElement) return;
      
      // Crear un canvas para capturar la foto
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0);
      
      // Convertir canvas a blob y luego a File
      canvas.toBlob((blob) => {
        if (blob) {
          // Crear un File desde el blob
          const file = new File([blob], `checkin-${optionValue}-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          
          // Guardar el archivo usando $set para asegurar reactividad
          this.$set(this.checkInPhotos, optionValue, file);
          // Forzar actualización para asegurar que Vue detecte el cambio
          this.$forceUpdate();
          
          // Crear vista previa
          const reader = new FileReader();
          reader.onload = (e) => {
            // Usar $set para asegurar reactividad
            this.$set(this.checkInPhotoPreviews, optionValue, e.target.result);
          };
          reader.readAsDataURL(file);
          
          // Cerrar la cámara
          this.closeCamera(optionValue);
        }
      }, 'image/jpeg', 0.9);
    },
    startRecordingVideo(optionValue) {
      const videoRef = `camera-${optionValue}`;
      const video = this.$refs[videoRef];
      
      if (!video || !this.activeCameraStream) return;
      
      const videoElement = Array.isArray(video) ? video[0] : video;
      if (!videoElement) return;
      
      // Limpiar vista previa anterior si existe
      if (this.checkInVideoPreviews[optionValue]) {
        this.$set(this.checkInVideoPreviews, optionValue, null);
      }
      if (this.checkInVideos[optionValue]) {
        this.$set(this.checkInVideos, optionValue, null);
      }
      
      // Asegurar que el video esté silenciado durante la grabación
      videoElement.muted = true;
      
      try {
        // Intentar usar codecs que soporten audio
        let mimeType = 'video/webm;codecs=vp9,opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm;codecs=vp8,opus';
        }
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
        
        const mediaRecorder = new MediaRecorder(this.activeCameraStream, {
          mimeType: mimeType
        });
        
        const chunks = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          const file = new File([blob], `checkin-${optionValue}-${Date.now()}.webm`, {
            type: 'video/webm'
          });
          
          // Guardar el archivo usando $set para asegurar reactividad
          this.$set(this.checkInVideos, optionValue, file);
          
          // Crear vista previa
          const reader = new FileReader();
          reader.onload = (e) => {
            this.$set(this.checkInVideoPreviews, optionValue, e.target.result);
          };
          reader.readAsDataURL(file);
          
          // Cerrar la cámara
          this.closeCamera(optionValue);
        };
        
        mediaRecorder.start();
        this.activeMediaRecorder = mediaRecorder;
        this.isRecordingVideo = true;
      } catch (error) {
        console.error('Error al iniciar grabación de video:', error);
        this.$bvToast.toast('Error al iniciar la grabación de video', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    stopRecordingVideo(optionValue) {
      if (this.activeMediaRecorder && this.isRecordingVideo) {
        this.activeMediaRecorder.stop();
        this.activeMediaRecorder = null;
        this.isRecordingVideo = false;
      }
    },
    closeCamera(optionValue) {
      // Detener la grabación si está activa
      if (this.activeMediaRecorder && this.isRecordingVideo) {
        this.activeMediaRecorder.stop();
        this.activeMediaRecorder = null;
        this.isRecordingVideo = false;
      }
      
      // Detener el stream de la cámara
      if (this.activeCameraStream) {
        this.activeCameraStream.getTracks().forEach(track => track.stop());
        this.activeCameraStream = null;
      }
      
      // Limpiar el video
      this.$nextTick(() => {
        const videoRef = `camera-${optionValue}`;
        const video = this.$refs[videoRef];
        if (video) {
          const videoElement = Array.isArray(video) ? video[0] : video;
          if (videoElement) {
            videoElement.srcObject = null;
          }
        }
      });
      
      this.activeCameraOption = null;
      this.activeCameraMode = null;
    },
    removeCheckInMedia(optionValue) {
      // Cerrar la cámara si está abierta
      if (this.activeCameraOption === optionValue) {
        this.closeCamera(optionValue);
      }
      
      // Crear nuevos objetos sin la propiedad para mantener reactividad
      const { [optionValue]: removedPhoto, ...restPhotos } = this.checkInPhotos;
      this.checkInPhotos = restPhotos;
      const { [optionValue]: removedPreview, ...restPreviews } = this.checkInPhotoPreviews;
      this.checkInPhotoPreviews = restPreviews;
      const { [optionValue]: removedVideo, ...restVideos } = this.checkInVideos;
      this.checkInVideos = restVideos;
      const { [optionValue]: removedVideoPreview, ...restVideoPreviews } = this.checkInVideoPreviews;
      this.checkInVideoPreviews = restVideoPreviews;
    },
    resetCheckInForm() {
      // Detener la grabación si está activa
      if (this.activeMediaRecorder && this.isRecordingVideo) {
        this.activeMediaRecorder.stop();
        this.activeMediaRecorder = null;
        this.isRecordingVideo = false;
      }
      
      // Cerrar la cámara si está abierta
      if (this.activeCameraStream) {
        this.activeCameraStream.getTracks().forEach(track => track.stop());
        this.activeCameraStream = null;
      }
      this.activeCameraOption = null;
      this.activeCameraMode = null;
      this.isCapturingPhoto = false;
      
      this.selectedCheckInItems = [];
      this.checkInPhotos = {};
      this.checkInPhotoPreviews = {};
      this.checkInVideos = {};
      this.checkInVideoPreviews = {};
      this.checkInOtherText = '';
      this.checkInError = '';
      this.isUploadingCheckIn = false;
    },
    async handleCheckInSubmit() {
      // Validar que "mascota" esté seleccionado (obligatorio)
      if (!this.selectedCheckInItems.includes('mascota')) {
        this.checkInError = 'Por favor, selecciona "Mascota" (obligatorio).';
        return;
      }
      
      // Validar que "mascota" tenga foto o video (obligatorio)
      const hasMascotaPhoto = !!this.checkInPhotoPreviews['mascota'];
      const hasMascotaVideo = !!this.checkInVideoPreviews['mascota'];
      if (!hasMascotaPhoto && !hasMascotaVideo) {
        this.checkInError = 'Por favor, agrega una foto o video para "Mascota" (obligatorio).';
        return;
      }
      
      // Validar que haya al menos un item seleccionado
      if (this.selectedCheckInItems.length === 0) {
        this.checkInError = 'Por favor, selecciona al menos un artículo.';
        return;
      }
      
      // Validar que si se seleccionó "otro", tenga texto
      if (this.selectedCheckInItems.includes('otro') && !this.checkInOtherText.trim()) {
        this.checkInError = 'Por favor, especifica qué elemento es en el campo "Otro".';
        return;
      }
      
      this.isUploadingCheckIn = true;
      this.checkInError = '';
      
      try {
        // Hacer una copia profunda de las fotos y videos al inicio para evitar problemas de reactividad
        const photosCopy = { ...this.checkInPhotos };
        const videosCopy = { ...this.checkInVideos };
        
        // Subir fotos/videos a Firebase Storage y construir la lista de evidencias
        // La foto/video es opcional, solo se incluyen items con media
        const evidenceList = [];
        
        for (const item of this.selectedCheckInItems) {
          this.loading = true;
          // Usar la copia en lugar del objeto reactivo
          const photoFile = photosCopy[item];
          const videoFile = videosCopy[item];
          
          // Determinar el nombre del item (si es "otro", usar el texto ingresado)
          const itemName = item === 'otro' ? this.checkInOtherText.trim() : item;
          
          if (photoFile) {
            // Si hay foto, subirla a Firebase Storage
            try {
              const photoUrl = await this.uploadCheckInPhoto(photoFile, item);
              
              evidenceList.push({
                name: itemName,
                photo: photoUrl,
                video: null
              });
            } catch (uploadError) {
              console.error(`Error al subir foto para ${item}:`, uploadError);
              // Continuar con el item pero sin URL
              evidenceList.push({
                name: itemName,
                photo: null,
                video: null
              });
            }
          } else if (videoFile) {
            // Si hay video, subirlo a Firebase Storage
            try {
              const videoUrl = await this.uploadCheckInVideo(videoFile, item);
              
              evidenceList.push({
                name: itemName,
                photo: null,
                video: videoUrl
              });
            } catch (uploadError) {
              console.error(`Error al subir video para ${item}:`, uploadError);
              // Continuar con el item pero sin URL
              evidenceList.push({
                name: itemName,
                photo: null,
                video: null
              });
            }
          } else {
            // Si no hay foto ni video, incluir el item sin URL (opcional)
            evidenceList.push({
              name: itemName,
              photo: null,
              video: null
            });
          }
        }
        
        // Transformar evidenceList al formato requerido (label, photo o video)
        const formattedEvidence = evidenceList.map(item => {
          const evidence = { label: item.name };
          if (item.photo) {
            evidence.photo = item.photo;
          } else if (item.video) {
            evidence.video = item.video;
          }
          return evidence;
        });
        
        // Llamar a la función de Firebase para crear la evidencia
        const createEvidenceDynamic = this.$firebase.functions().httpsCallable('createEvidenceDynamic');
        const payload = {
          serviceId: this.service.id,
          evidence: formattedEvidence,
          checkIn: true,
          type: TYPES.CHECK_IN,
        };
        
        const { data: response } = await createEvidenceDynamic(payload);
        
        // Obtener y enviar posición GPS a demanda
        try {
          await this.sendGPSPosition('checkin');
        } catch (error) {
          console.error('Error al enviar GPS en check-in:', error);
          // No bloquear el flujo si falla el GPS
        }
        
        this.$bvToast.toast('Check-in guardado correctamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        });
        
        this.showCheckInModal = false;
        this.resetCheckInForm();
        
        // Refrescar el servicio para actualizar has_check_in
        await this.refreshService();
        
      } catch (error) {
        console.error('Error al guardar check-in:', error);
        this.checkInError = 'Error al guardar el check-in. Por favor, intenta nuevamente.';
      } finally {
        this.isUploadingCheckIn = false;
      }
    },
    async uploadCheckInPhoto(file, itemName) {
      return new Promise((resolve, reject) => {
        try {
          const serviceId = this.service.id;
          const timestamp = Date.now();
          const fileName = `checkin_${itemName}_${timestamp}_${file.name}`;
          const path = `services/${serviceId}/checkin/${fileName}`;
          
          const fileTask = this.$firebase.storage().ref(path).put(file);
          
          fileTask.on(
            this.$firebase.storage.TaskEvent.STATE_CHANGED,
            (snapshot) => {
              // Progreso de subida (opcional)
            },
            (error) => {
              console.error(`Error subiendo foto de ${itemName}:`, error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await fileTask.snapshot.ref.getDownloadURL();
                resolve(downloadURL);
              } catch (error) {
                console.error(`Error obteniendo URL de ${itemName}:`, error);
                reject(error);
              }
            }
          );
        } catch (error) {
        reject(error);
      }
    });
  },
  async uploadCheckInVideo(file, itemName) {
    return new Promise((resolve, reject) => {
      try {
        const serviceId = this.service.id;
        const timestamp = Date.now();
        const fileName = `checkin_${itemName}_${timestamp}_${file.name}`;
        const path = `services/${serviceId}/checkin/${fileName}`;
        
        const fileTask = this.$firebase.storage().ref(path).put(file);
        
        fileTask.on(
          this.$firebase.storage.TaskEvent.STATE_CHANGED,
          (snapshot) => {
            // Progreso de subida (opcional)
          },
          (error) => {
            console.error(`Error subiendo video de ${itemName}:`, error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await fileTask.snapshot.ref.getDownloadURL();
              resolve(downloadURL);
            } catch (error) {
              console.error(`Error obteniendo URL de ${itemName}:`, error);
              reject(error);
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  },
  // Métodos para Evidencia
  async openEvidenceCamera(mode = 'photo') {
    this.isCapturingEvidencePhoto = true;
    this.activeEvidenceCamera = true;
    this.activeEvidenceCameraMode = mode;
    
    // Si se abre en modo video, limpiar vista previa anterior
    if (mode === 'video') {
      this.evidenceVideoPreview = null;
      this.evidenceVideo = null;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment'
        },
        audio: true // Incluir audio para videos
      });
      
      this.activeEvidenceCameraStream = stream;
      
      this.$nextTick(() => {
        const video = this.$refs['evidence-camera'];
        if (video) {
          const videoElement = Array.isArray(video) ? video[0] : video;
          if (videoElement) {
            videoElement.srcObject = stream;
            // Asegurar que esté silenciado durante la grabación
            videoElement.muted = true;
          }
        }
      });
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
      this.$bvToast.toast('Error al acceder a la cámara. Por favor, verifica los permisos.', {
        title: 'Error',
        variant: 'danger',
        solid: true
      });
      this.activeEvidenceCamera = false;
      this.activeEvidenceCameraStream = null;
      this.activeEvidenceCameraMode = null;
    } finally {
      this.isCapturingEvidencePhoto = false;
    }
  },
  captureEvidencePhoto() {
    const video = this.$refs['evidence-camera'];
    
    if (!video) return;
    
    const videoElement = Array.isArray(video) ? video[0] : video;
    if (!videoElement) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoElement, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `evidence-${Date.now()}.jpg`, {
          type: 'image/jpeg'
        });
        
        this.evidencePhoto = file;
        
        const reader = new FileReader();
        reader.onload = (e) => {
          this.evidencePhotoPreview = e.target.result;
        };
        reader.readAsDataURL(file);
        
        this.closeEvidenceCamera();
      }
    }, 'image/jpeg', 0.9);
  },
  startRecordingEvidenceVideo() {
    const video = this.$refs['evidence-camera'];
    
    if (!video || !this.activeEvidenceCameraStream) return;
    
    const videoElement = Array.isArray(video) ? video[0] : video;
    if (!videoElement) return;
    
    // Limpiar vista previa anterior si existe
    this.evidenceVideoPreview = null;
    this.evidenceVideo = null;
    
    // Asegurar que el video esté silenciado durante la grabación
    videoElement.muted = true;
    
    try {
      // Intentar usar codecs que soporten audio
      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8,opus';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
      }
      
      const mediaRecorder = new MediaRecorder(this.activeEvidenceCameraStream, {
        mimeType: mimeType
      });
      
      const chunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const file = new File([blob], `evidence-${Date.now()}.webm`, {
          type: 'video/webm'
        });
        
        this.evidenceVideo = file;
        
        const reader = new FileReader();
        reader.onload = (e) => {
          this.evidenceVideoPreview = e.target.result;
        };
        reader.readAsDataURL(file);
        
        this.closeEvidenceCamera();
      };
      
      mediaRecorder.start();
      this.activeEvidenceMediaRecorder = mediaRecorder;
      this.isRecordingEvidenceVideo = true;
    } catch (error) {
      console.error('Error al iniciar grabación de video:', error);
      this.$bvToast.toast('Error al iniciar la grabación de video', {
        title: 'Error',
        variant: 'danger',
        solid: true
      });
    }
  },
  stopRecordingEvidenceVideo() {
    if (this.activeEvidenceMediaRecorder && this.isRecordingEvidenceVideo) {
      this.activeEvidenceMediaRecorder.stop();
      this.activeEvidenceMediaRecorder = null;
      this.isRecordingEvidenceVideo = false;
    }
  },
  closeEvidenceCamera() {
    // Detener la grabación si está activa
    if (this.activeEvidenceMediaRecorder && this.isRecordingEvidenceVideo) {
      this.activeEvidenceMediaRecorder.stop();
      this.activeEvidenceMediaRecorder = null;
      this.isRecordingEvidenceVideo = false;
    }
    
    if (this.activeEvidenceCameraStream) {
      this.activeEvidenceCameraStream.getTracks().forEach(track => track.stop());
      this.activeEvidenceCameraStream = null;
    }
    
    this.$nextTick(() => {
      const video = this.$refs['evidence-camera'];
      if (video) {
        const videoElement = Array.isArray(video) ? video[0] : video;
        if (videoElement) {
          videoElement.srcObject = null;
        }
      }
    });
    
    this.activeEvidenceCamera = false;
    this.activeEvidenceCameraMode = null;
  },
  removeEvidenceMedia() {
    this.evidencePhoto = null;
    this.evidencePhotoPreview = null;
    this.evidenceVideo = null;
    this.evidenceVideoPreview = null;
  },
  resetEvidenceForm() {
    // Detener la grabación si está activa
    if (this.activeEvidenceMediaRecorder && this.isRecordingEvidenceVideo) {
      this.activeEvidenceMediaRecorder.stop();
      this.activeEvidenceMediaRecorder = null;
      this.isRecordingEvidenceVideo = false;
    }
    
    if (this.activeEvidenceCameraStream) {
      this.activeEvidenceCameraStream.getTracks().forEach(track => track.stop());
      this.activeEvidenceCameraStream = null;
    }
    this.activeEvidenceCamera = false;
    this.activeEvidenceCameraMode = null;
    this.isCapturingEvidencePhoto = false;
    
    this.evidenceComment = '';
    this.evidencePhoto = null;
    this.evidencePhotoPreview = null;
    this.evidenceVideo = null;
    this.evidenceVideoPreview = null;
    this.evidenceError = '';
    this.isUploadingEvidence = false;
  },
  async handleEvidenceSubmit() {
    if (!this.evidenceComment.trim()) {
      this.evidenceError = 'Por favor, escribe un comentario.';
      return;
    }
    
    this.isUploadingEvidence = true;
    this.evidenceError = '';
    
    // Guardar el comentario antes de limpiar el formulario
    const commentText = this.evidenceComment.trim();
    
    try {
      let photoUrl = null;
      let videoUrl = null;
      this.loading = true;
      
      // Si hay foto, subirla a Firebase Storage
      if (this.evidencePhoto) {
        photoUrl = await this.uploadEvidencePhoto(this.evidencePhoto);
      }
      // Si hay video, subirlo a Firebase Storage
      else if (this.evidenceVideo) {
        videoUrl = await this.uploadEvidenceVideo(this.evidenceVideo);
      }
      
      // Construir el payload usando el comentario guardado
      const formattedEvidence = [{
        label: commentText,
        photo: photoUrl || null,
        video: videoUrl || null
      }];
      
      // Llamar a la función de Firebase
      const createEvidenceDynamic = this.$firebase.functions().httpsCallable('createEvidenceDynamic');
      const payload = {
        serviceId: this.service.id,
        evidence: formattedEvidence,
        checkIn: false,
        type: TYPES.EVIDENCE,
      };
      
      const { data: response } = await createEvidenceDynamic(payload);
      
      // Obtener y enviar posición GPS a demanda
      try {
        await this.sendGPSPosition('evidencia');
      } catch (error) {
        console.error('Error al enviar GPS en evidencia:', error);
        // No bloquear el flujo si falla el GPS
      }
      
      this.$bvToast.toast('Evidencia guardada correctamente', {
        title: 'Éxito',
        variant: 'success',
        solid: true
      });
      
      this.showEvidenceModal = false;
      this.resetEvidenceForm();
      
      // Refrescar el servicio
      await this.refreshService();
      
    } catch (error) {
      console.error('Error al guardar evidencia:', error);
      this.evidenceError = 'Error al guardar la evidencia. Por favor, intenta nuevamente.';
    } finally {
      this.isUploadingEvidence = false;
      this.loading = false;
    }
  },
  async uploadEvidencePhoto(file) {
    return new Promise((resolve, reject) => {
      try {
        const serviceId = this.service.id;
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        
        // Obtener año y mes actual en formato YYYY-MM usando moment
        const dateFolder = moment().format('YYYY-MM');
        
        const path = `services-evidence/${dateFolder}/${serviceId}/${fileName}`;
        
        const fileTask = this.$firebase.storage().ref(path).put(file);
        
        fileTask.on(
          this.$firebase.storage.TaskEvent.STATE_CHANGED,
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          },
          (error) => {
            console.error('Error subiendo foto de evidencia:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await fileTask.snapshot.ref.getDownloadURL();
              resolve(downloadURL);
            } catch (error) {
              console.error('Error obteniendo URL de evidencia:', error);
              reject(error);
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  },
  async uploadEvidenceVideo(file) {
    return new Promise((resolve, reject) => {
      try {
        const serviceId = this.service.id;
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        
        // Obtener año y mes actual en formato YYYY-MM usando moment
        const dateFolder = moment().format('YYYY-MM');
        
        const path = `services-evidence/${dateFolder}/${serviceId}/${fileName}`;
        
        const fileTask = this.$firebase.storage().ref(path).put(file);
        
        fileTask.on(
          this.$firebase.storage.TaskEvent.STATE_CHANGED,
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          },
          (error) => {
            console.error('Error subiendo video de evidencia:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await fileTask.snapshot.ref.getDownloadURL();
              resolve(downloadURL);
            } catch (error) {
              console.error('Error obteniendo URL de evidencia:', error);
              reject(error);
            }
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  },
  formatDate(dateString) {
    if (!dateString) return '';
    return moment(dateString, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY HH:mm');
  },
  formatTimeForBitacora(dateString) {
    if (!dateString) return '';
    const m = this.parseServiceDateToMoment(dateString);
    return m && m.isValid() ? m.format('HH:mm') : '';
  },
  parseServiceDate(dateString) {
    if (!dateString) return null;
    const m = this.parseServiceDateToMoment(dateString);
    if (!m || !m.isValid()) return null;
    const d = m.toDate();
    return isNaN(d.getTime()) ? null : d;
  },
  // Soporta created_at "12/01/2026 07:31:12 PM" (12h) e ISO; devuelve moment o null
  parseServiceDateToMoment(dateString) {
    if (!dateString) return null;
    const s = String(dateString).trim();
    if (!s) return null;
    const formats = [
      'DD/MM/YYYY hh:mm:ss A',
      'DD/MM/YYYY HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'YYYY-MM-DDTHH:mm:ssZ'
    ];
    for (const fmt of formats) {
      const m = moment(s, fmt, true);
      if (m.isValid()) return m;
    }
    const fallback = moment(s);
    return fallback.isValid() ? fallback : null;
  },
  openImageFullscreen(imageUrl, label = null) {
    this.fullscreenImageUrl = imageUrl;
    this.fullscreenVideoUrl = null;
    this.fullscreenImageLabel = label;
    this.fullscreenMediaType = 'image';
    this.showFullscreenImage = true;
  },
  openVideoFullscreen(videoUrl, label = null) {
    this.fullscreenImageUrl = null;
    this.fullscreenVideoUrl = videoUrl;
    this.fullscreenImageLabel = label;
    this.fullscreenMediaType = 'video';
    this.showFullscreenImage = true;
  },
  closeFullscreenImage() {
    this.showFullscreenImage = false;
    this.fullscreenImageUrl = null;
    this.fullscreenVideoUrl = null;
    this.fullscreenImageLabel = null;
    this.fullscreenMediaType = null;
  },
  async copyServiceId() {
    if (!this.service || !this.service.id) {
      return;
    }
    
    try {
      // Intentar usar la API moderna de clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(this.service.id.toString());
      } else {
        // Fallback para navegadores antiguos
        const textArea = document.createElement('textarea');
        textArea.value = this.service.id.toString();
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      this.$bvToast.toast('ID copiado al portapapeles', {
        title: 'Éxito',
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      });
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error);
      this.$bvToast.toast('Error al copiar el ID', {
        title: 'Error',
        variant: 'danger',
        solid: true
      });
    }
  },
    async openCheckOutCamera(label, mode = 'photo') {
      this.isCapturingCheckOutPhoto = true;
      this.activeCheckOutCameraOption = label;
      this.activeCheckOutCameraMode = mode;
      
      // Si se abre en modo video, limpiar vista previa anterior
      if (mode === 'video') {
        if (this.checkOutVideoPreviews[label]) {
          this.$set(this.checkOutVideoPreviews, label, null);
        }
        if (this.checkOutVideos[label]) {
          this.$set(this.checkOutVideos, label, null);
        }
      }
      
      try {
        // Solicitar acceso a la cámara y micrófono
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment' // Cámara trasera en móviles
          },
          audio: true // Incluir audio para videos
        });
        
        this.activeCheckOutCameraStream = stream;
        
        // Mostrar el video en el elemento video
        this.$nextTick(() => {
          const videoRefName = label === 'custom-comment' ? 'checkout-camera-custom-comment' : `checkout-camera-${label}`;
          const video = this.$refs[videoRefName];
          if (video) {
            const videoElement = Array.isArray(video) ? video[0] : video;
            if (videoElement) {
              videoElement.srcObject = stream;
              // Asegurar que esté silenciado durante la grabación
              videoElement.muted = true;
            }
          }
        });
      } catch (error) {
        console.error('Error al acceder a la cámara:', error);
        this.$bvToast.toast('Error al acceder a la cámara. Por favor, verifica los permisos.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        this.activeCheckOutCameraOption = null;
        this.activeCheckOutCameraStream = null;
        this.activeCheckOutCameraMode = null;
      } finally {
        this.isCapturingCheckOutPhoto = false;
      }
    },
    captureCheckOutPhoto(label) {
      const videoRefName = label === 'custom-comment' ? 'checkout-camera-custom-comment' : `checkout-camera-${label}`;
      const video = this.$refs[videoRefName];
      
      if (!video) return;
      
      const videoElement = Array.isArray(video) ? video[0] : video;
      if (!videoElement) return;
      
      // Crear un canvas para capturar la foto
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0);
      
      // Convertir canvas a blob y luego a File
      canvas.toBlob((blob) => {
        if (blob) {
          // Crear un File desde el blob
          const file = new File([blob], `checkout-${label}-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          
          // Guardar el archivo usando $set para asegurar reactividad
          this.$set(this.checkOutPhotos, label, file);
          // Forzar actualización para asegurar que Vue detecte el cambio
          this.$forceUpdate();
          
          // Crear vista previa
          const reader = new FileReader();
          reader.onload = (e) => {
            // Usar $set para asegurar reactividad
            this.$set(this.checkOutPhotoPreviews, label, e.target.result);
          };
          reader.readAsDataURL(file);
          
          // Cerrar la cámara
          this.closeCheckOutCamera(label);
        }
      }, 'image/jpeg', 0.9);
    },
    startRecordingCheckOutVideo(label) {
      const videoRefName = label === 'custom-comment' ? 'checkout-camera-custom-comment' : `checkout-camera-${label}`;
      const video = this.$refs[videoRefName];
      
      if (!video || !this.activeCheckOutCameraStream) return;
      
      const videoElement = Array.isArray(video) ? video[0] : video;
      if (!videoElement) return;
      
      // Limpiar vista previa anterior si existe
      if (this.checkOutVideoPreviews[label]) {
        this.$set(this.checkOutVideoPreviews, label, null);
      }
      if (this.checkOutVideos[label]) {
        this.$set(this.checkOutVideos, label, null);
      }
      
      // Asegurar que el video esté silenciado durante la grabación
      videoElement.muted = true;
      
      try {
        // Intentar usar codecs que soporten audio
        let mimeType = 'video/webm;codecs=vp9,opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm;codecs=vp8,opus';
        }
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
        
        const mediaRecorder = new MediaRecorder(this.activeCheckOutCameraStream, {
          mimeType: mimeType
        });
        
        const chunks = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          const file = new File([blob], `checkout-${label}-${Date.now()}.webm`, {
            type: 'video/webm'
          });
          
          // Guardar el archivo usando $set para asegurar reactividad
          this.$set(this.checkOutVideos, label, file);
          
          // Crear vista previa
          const reader = new FileReader();
          reader.onload = (e) => {
            this.$set(this.checkOutVideoPreviews, label, e.target.result);
          };
          reader.readAsDataURL(file);
          
          // Cerrar la cámara
          this.closeCheckOutCamera(label);
        };
        
        mediaRecorder.start();
        this.activeCheckOutMediaRecorder = mediaRecorder;
        this.isRecordingCheckOutVideo = true;
      } catch (error) {
        console.error('Error al iniciar grabación de video:', error);
        this.$bvToast.toast('Error al iniciar la grabación de video', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    stopRecordingCheckOutVideo(label) {
      if (this.activeCheckOutMediaRecorder && this.isRecordingCheckOutVideo) {
        this.activeCheckOutMediaRecorder.stop();
        this.activeCheckOutMediaRecorder = null;
        this.isRecordingCheckOutVideo = false;
      }
    },
    closeCheckOutCamera(label) {
      // Detener la grabación si está activa
      if (this.activeCheckOutMediaRecorder && this.isRecordingCheckOutVideo) {
        this.activeCheckOutMediaRecorder.stop();
        this.activeCheckOutMediaRecorder = null;
        this.isRecordingCheckOutVideo = false;
      }
      
      // Detener el stream de la cámara
      if (this.activeCheckOutCameraStream) {
        this.activeCheckOutCameraStream.getTracks().forEach(track => track.stop());
        this.activeCheckOutCameraStream = null;
      }
      
      // Limpiar el video
      this.$nextTick(() => {
        const videoRefName = label === 'custom-comment' ? 'checkout-camera-custom-comment' : `checkout-camera-${label}`;
        const video = this.$refs[videoRefName];
        if (video) {
          const videoElement = Array.isArray(video) ? video[0] : video;
          if (videoElement) {
            videoElement.srcObject = null;
          }
        }
      });
      
      this.activeCheckOutCameraOption = null;
      this.activeCheckOutCameraMode = null;
    },
    removeCheckOutMedia(label) {
      // Cerrar la cámara si está abierta
      if (this.activeCheckOutCameraOption === label) {
        this.closeCheckOutCamera(label);
      }
      
      // Crear nuevos objetos sin la propiedad para mantener reactividad
      const { [label]: removedPhoto, ...restPhotos } = this.checkOutPhotos;
      this.checkOutPhotos = restPhotos;
      const { [label]: removedPreview, ...restPreviews } = this.checkOutPhotoPreviews;
      this.checkOutPhotoPreviews = restPreviews;
      const { [label]: removedVideo, ...restVideos } = this.checkOutVideos;
      this.checkOutVideos = restVideos;
      const { [label]: removedVideoPreview, ...restVideoPreviews } = this.checkOutVideoPreviews;
      this.checkOutVideoPreviews = restVideoPreviews;
    },
    resetCheckOutForm() {
      // Detener la grabación si está activa
      if (this.activeCheckOutMediaRecorder && this.isRecordingCheckOutVideo) {
        this.activeCheckOutMediaRecorder.stop();
        this.activeCheckOutMediaRecorder = null;
        this.isRecordingCheckOutVideo = false;
      }
      
      // Cerrar la cámara si está abierta
      if (this.activeCheckOutCameraStream) {
        this.activeCheckOutCameraStream.getTracks().forEach(track => track.stop());
        this.activeCheckOutCameraStream = null;
      }
      this.activeCheckOutCameraOption = null;
      this.activeCheckOutCameraMode = null;
      this.isCapturingCheckOutPhoto = false;
      
      this.checkOutPhotos = {};
      this.checkOutPhotoPreviews = {};
      this.checkOutVideos = {};
      this.checkOutVideoPreviews = {};
      this.checkOutComment = '';
      this.checkOutError = '';
      this.isUploadingCheckOut = false;
    },
    async handleCheckOutSubmit() {
      // Validar que todos los items tengan foto o video
      if (!this.hasAllCheckOutMedia) {
        this.checkOutError = 'Por favor, agrega una foto o video para todos los artículos.';
        return;
      }
      
      // Capturar el valor del comentario AL INICIO, antes de cualquier otra operación
      const capturedComment = this.checkOutComment ? String(this.checkOutComment).trim() : '';
      
      this.isUploadingCheckOut = true;
      this.checkOutError = '';
      
      try {
        // Hacer una copia profunda de las fotos y videos al inicio para evitar problemas de reactividad
        const photosCopy = { ...this.checkOutPhotos };
        const videosCopy = { ...this.checkOutVideos };
        
        // Subir fotos/videos a Firebase Storage y construir la lista de evidencias
        const evidenceList = [];
        
        for (const label of this.checkOutItems) {
          this.loading = true;
          // Usar la copia en lugar del objeto reactivo
          const photoFile = photosCopy[label];
          const videoFile = videosCopy[label];
          
          if (photoFile) {
            // Si hay foto, subirla a Firebase Storage
            try {
              const photoUrl = await this.uploadCheckOutPhoto(photoFile, label);
              
              evidenceList.push({
                label: label,
                photo: photoUrl,
                video: null
              });
            } catch (uploadError) {
              console.error(`Error al subir foto para ${label}:`, uploadError);
              // Continuar con el item pero sin URL
              evidenceList.push({
                label: label,
                photo: null,
                video: null
              });
            }
          } else if (videoFile) {
            // Si hay video, subirlo a Firebase Storage
            try {
              const videoUrl = await this.uploadCheckOutVideo(videoFile, label);
              
              evidenceList.push({
                label: label,
                photo: null,
                video: videoUrl
              });
            } catch (uploadError) {
              console.error(`Error al subir video para ${label}:`, uploadError);
              // Continuar con el item pero sin URL
              evidenceList.push({
                label: label,
                photo: null,
                video: null
              });
            }
          }
        }
        
        // Agregar item personalizado (comentario) si tiene contenido (label, foto o video)
        // Usar el valor capturado al inicio del método
        const customCommentText = capturedComment;
        const customCommentPhoto = photosCopy['custom-comment'];
        const customCommentVideo = videosCopy['custom-comment'];
        
        // Solo agregar si tiene texto, foto o video
        if (customCommentText || customCommentPhoto || customCommentVideo) {
          // SIEMPRE usar el texto escrito como label si existe, de lo contrario usar label por defecto solo si hay foto/video
          const customCommentLabel = customCommentText || (customCommentPhoto || customCommentVideo ? 'Comentario adicional' : '');
          
          // Si hay foto, subirla y agregar el item
          if (customCommentPhoto) {
            try {
              const photoUrl = await this.uploadCheckOutPhoto(customCommentPhoto, 'custom-comment');
              evidenceList.push({
                label: customCommentLabel,
                photo: photoUrl,
                video: null
              });
            } catch (uploadError) {
              console.error('Error al subir foto del comentario:', uploadError);
              // Agregar el item sin foto si hay texto o label
              if (customCommentLabel) {
                evidenceList.push({
                  label: customCommentLabel,
                  photo: null,
                  video: null
                });
              }
            }
          } 
          // Si hay video, subirlo y agregar el item
          else if (customCommentVideo) {
            try {
              const videoUrl = await this.uploadCheckOutVideo(customCommentVideo, 'custom-comment');
              evidenceList.push({
                label: customCommentLabel,
                photo: null,
                video: videoUrl
              });
            } catch (uploadError) {
              console.error('Error al subir video del comentario:', uploadError);
              // Agregar el item sin video si hay texto o label
              if (customCommentLabel) {
                evidenceList.push({
                  label: customCommentLabel,
                  photo: null,
                  video: null
                });
              }
            }
          } 
          // Solo tiene texto, sin foto ni video
          else if (customCommentText) {
            evidenceList.push({
              label: customCommentText,
              photo: null,
              video: null
            });
          }
        }
        
        // Llamar a la función de Firebase para crear la evidencia
        const createEvidenceDynamic = this.$firebase.functions().httpsCallable('createEvidenceDynamic');
        const payload = {
          serviceId: this.service.id,
          evidence: evidenceList,
          checkIn: false,
          type: TYPES.CHECK_OUT,
        };
        
        const { data: response } = await createEvidenceDynamic(payload);
        
        // Obtener y enviar posición GPS a demanda
        try {
          await this.sendGPSPosition('checkout');
        } catch (error) {
          console.error('Error al enviar GPS en checkout:', error);
          // No bloquear el flujo si falla el GPS
        }
        
        this.$bvToast.toast('Check-out guardado correctamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        });
        
        this.showCheckOutModal = false;
        this.resetCheckOutForm();
        
        // Refrescar el servicio para actualizar evidencias
        await this.refreshService();
        
      } catch (error) {
        console.error('Error al guardar check-out:', error);
        this.checkOutError = 'Error al guardar el check-out. Por favor, intenta nuevamente.';
      } finally {
        this.isUploadingCheckOut = false;
      }
    },
    async uploadCheckOutPhoto(file, label) {
      return new Promise((resolve, reject) => {
        try {
          const serviceId = this.service.id;
          const timestamp = Date.now();
          const fileName = `checkout_${label}_${timestamp}_${file.name}`;
          const path = `services/${serviceId}/checkout/${fileName}`;
          
          const fileTask = this.$firebase.storage().ref(path).put(file);
          
          fileTask.on(
            this.$firebase.storage.TaskEvent.STATE_CHANGED,
            (snapshot) => {
              // Progreso de subida (opcional)
            },
            (error) => {
              console.error(`Error subiendo foto de ${label}:`, error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await fileTask.snapshot.ref.getDownloadURL();
                resolve(downloadURL);
              } catch (error) {
                console.error(`Error obteniendo URL de ${label}:`, error);
                reject(error);
              }
            }
          );
        } catch (error) {
          reject(error);
        }
      });
    },
    async uploadCheckOutVideo(file, label) {
      return new Promise((resolve, reject) => {
        try {
          const serviceId = this.service.id;
          const timestamp = Date.now();
          const fileName = `checkout_${label}_${timestamp}_${file.name}`;
          const path = `services/${serviceId}/checkout/${fileName}`;
          
          const fileTask = this.$firebase.storage().ref(path).put(file);
          
          fileTask.on(
            this.$firebase.storage.TaskEvent.STATE_CHANGED,
            (snapshot) => {
              // Progreso de subida (opcional)
            },
            (error) => {
              console.error(`Error subiendo video de ${label}:`, error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await fileTask.snapshot.ref.getDownloadURL();
                resolve(downloadURL);
              } catch (error) {
                console.error(`Error obteniendo URL de ${label}:`, error);
                reject(error);
              }
            }
          );
        } catch (error) {
          reject(error);
        }
      });
    }
  }
}
</script>

<style scoped>
  .service-detail-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
  }
  
  .service-counter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 0.75rem;
    font-weight: bold;
    flex-shrink: 0;
  }
  
  .service-detail-card h6 {
    font-size: 1rem;
  }
  
  @media (max-width: 576px) {
    .service-detail-card .card-body {
      padding-top: 0.75rem !important;
      padding-left: 0.75rem !important;
      padding-right: 0.75rem !important;
      padding-bottom: 0.5rem !important;
    }
    
    .service-detail-card h6 {
      font-size: 0.9rem;
    }
  }
  
  /* FAB Container */
  .fab-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
  }
  
  /* Overlay para cerrar el menú */
  .fab-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 999;
  }
  
  /* Botones FAB */
  .fab-button {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 1001;
  }
  
  .fab-button.primary {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4);
  }
  
  .fab-button.secondary {
    width: 48px;
    height: 48px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  }
  
  .fab-button:hover:not(:disabled) {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.5);
    transform: scale(1.1);
  }
  
  .fab-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  /* Contenedor de items FAB */
  .fab-items {
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  
  /* Item individual del FAB */
  .fab-item {
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    z-index: 1001;
  }
  
  /* Label del FAB */
  .fab-label {
    background-color: rgba(0, 0, 0, 0.87);
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
    margin-right: 12px;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  /* Transiciones para los items */
  .fab-item-enter-active,
  .fab-item-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .fab-item-enter {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  
  .fab-item-enter-to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  
  .fab-item-leave {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  
  .fab-item-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }

  /* Bitácora del servicio (WFStates + evidencias cronológicos) */
  .bitacora-card .bitacora-header {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    padding: 1rem 1.25rem;
    color: #fff;
    border-radius: 0;
  }
  .bitacora-card .bitacora-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }
  .bitacora-body {
    padding: 1.25rem;
    max-height: 500px;
    overflow-y: auto;
  }
  .bitacora-timeline {
    position: relative;
    padding-left: 0;
  }
  .bitacora-item {
    position: relative;
    padding-bottom: 1.5rem;
    padding-left: 4rem;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }
  .bitacora-item:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 1.25rem;
    top: 2.5rem;
    bottom: -1.5rem;
    width: 2px;
    background: #e0e0e0;
  }
  .bitacora-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    position: absolute;
    left: 0;
    color: #fff;
  }
  .bitacora-icon-success { background: #4caf50; }
  .bitacora-icon-info { background: #2196f3; }
  .bitacora-icon-warning { background: #ff9800; }
  .bitacora-icon-danger { background: #bf0811; }
  .bitacora-content { flex: 1; padding-top: 0; }
  .bitacora-time {
    font-weight: 600;
    color: #2c3e50;
    font-size: 0.9rem;
    white-space: nowrap;
    margin-bottom: 0.25rem;
  }
  .bitacora-label {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    line-height: 1.5;
  }
  .bitacora-emoji { font-size: 1.25rem; display: inline-block; line-height: 1; }
  .bitacora-link a { color: #2196f3; text-decoration: none; font-weight: 500; }
  .bitacora-link a:hover { color: #1976d2; text-decoration: underline; }
  .bitacora-photo-link {
    color: #2196f3;
    cursor: pointer;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
  }
  .bitacora-photo-link:hover { color: #1976d2; text-decoration: underline; }
  .bitacora-empty { text-align: center; padding: 2rem; }

  /* Estilos para el modal de Check In - Material Design */
  .checkin-modal-header {
    border-bottom: none;
    padding: 1.25rem 1.5rem 0.5rem;
  }

  .checkin-modal-body {
    padding: 0 1.5rem 1rem;
  }

  .checkin-modal-footer {
    border-top: 1px solid #e0e0e0;
    padding: 1rem 1.5rem;
  }

  .checkin-form {
    padding: 0;
  }

  .checkin-description {
    color: #666;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  /* Cards estilo Material Design */
  .checkin-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .checkin-card {
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 1rem;
    transition: all 0.2s ease;
  }

  .checkin-card-selected {
    background-color: #fff;
    border-color: #2196F3;
    box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
  }

  .checkin-card-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .checkin-card-left {
    flex: 1;
    min-width: 0;
  }

  .checkin-card-right {
    flex-shrink: 0;
  }

  .checkin-checkbox {
    margin-bottom: 0.5rem;
  }

  .checkin-checkbox .custom-control-label {
    font-weight: 500;
    font-size: 1rem;
    color: #333;
    cursor: pointer;
  }

  .checkin-label {
    font-weight: 500;
    font-size: 1rem;
    color: #333;
  }

  .checkin-photo-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #4caf50;
  }

  .checkin-check-icon {
    color: #4caf50;
    font-size: 1rem;
  }

  .checkin-photo-text {
    color: #4caf50;
    font-weight: 400;
  }

  .checkin-custom-input {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
  }

  /* Vista previa de foto (miniatura) */
  .checkin-photo-thumbnail {
    position: relative;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #e0e0e0;
    cursor: pointer;
  }

  .checkin-thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .checkin-thumbnail-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .checkin-photo-thumbnail:hover .checkin-thumbnail-overlay {
    opacity: 1;
  }

  .checkin-thumbnail-btn {
    padding: 0.25rem;
    color: #fff;
    border: none;
    background: transparent;
  }

  .checkin-thumbnail-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  /* Botón agregar foto */
  .checkin-add-photo {
    display: flex;
    align-items: center;
  }

  .checkin-add-photo-btn {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }

  /* Placeholder de cámara */
  .checkin-camera-placeholder {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: #f5f5f5;
    border: 2px dashed #e0e0e0;
  }

  .checkin-placeholder-icon {
    font-size: 1.5rem;
    color: #999;
  }

  /* Vista previa de cámara */
  .checkin-camera-preview-container {
    border-top: 1px solid #e0e0e0;
    padding-top: 1rem;
  }

  .checkin-camera-preview {
    width: 100%;
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    background-color: #000;
    object-fit: contain;
  }

  .checkin-photo-preview {
    width: 100%;
    max-width: 100%;
    max-height: 400px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    object-fit: contain;
    display: block;
  }

  .evidence-photo-preview {
    width: 100%;
    overflow: hidden;
  }

  .checkin-camera-controls {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
  }

  /* Botón continuar */
  .checkin-continue-btn {
    border-radius: 8px;
    padding: 0.625rem 1.5rem;
    font-weight: 500;
  }

  /* Responsive */
  @media (max-width: 576px) {
    .checkin-card-content {
      flex-direction: column;
    }

    .checkin-card-right {
      width: 100%;
      display: flex;
      justify-content: flex-start;
    }

    .checkin-photo-thumbnail,
    .checkin-camera-placeholder {
      width: 60px;
      height: 60px;
    }
  }

  /* Estilos para Check-In realizado */
  .checkin-status {
    display: inline-flex;
    align-items: center;
  }

  .checkin-check-icon-green {
    color: #4caf50;
    font-size: 1.2rem;
  }

  .checkin-detail-link {
    color: #2196F3;
    text-decoration: none;
    margin-left: 0.5rem;
    font-weight: 500;
  }

  .checkin-detail-link:hover {
    text-decoration: underline;
  }

  /* Estilos para el detalle de Check-In */
  .checkin-detail-items {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .checkin-detail-item {
    width: 100%;
  }

  .checkin-detail-card {
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 1rem;
  }

  .checkin-detail-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .checkin-detail-left {
    flex: 1;
  }

  .checkin-detail-label {
    font-size: 1rem;
    color: #333;
  }

  .checkin-detail-right {
    flex-shrink: 0;
  }

  .checkin-detail-photo-thumbnail {
    position: relative;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #e0e0e0;
    cursor: pointer;
  }

  .checkin-detail-no-photo {
    width: 80px;
    height: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: #f5f5f5;
    border: 2px dashed #e0e0e0;
  }

  /* Estilos para lista de evidencias */
  .evidence-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .evidence-item {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 1rem;
  }

  .evidence-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .evidence-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .evidence-item-info {
    flex: 1;
  }

  .evidence-label {
    font-size: 1rem;
    color: #333;
    display: block;
    margin-bottom: 0.25rem;
  }

  .evidence-photo-thumbnail {
    position: relative;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #e0e0e0;
    cursor: pointer;
    flex-shrink: 0;
  }

  .evidence-thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .evidence-thumbnail-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .evidence-photo-thumbnail:hover .evidence-thumbnail-overlay {
    opacity: 1;
  }

  .evidence-no-photo {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: #f5f5f5;
    border: 2px dashed #e0e0e0;
    flex-shrink: 0;
  }

  /* Modal Fullscreen para Imágenes */
  .fullscreen-image-modal .modal-dialog {
    max-width: 95vw;
    height: 95vh;
    margin: 2.5vh auto;
  }

  .fullscreen-image-modal .modal-content {
    height: 100%;
    background-color: rgba(0, 0, 0, 0.95);
    border: none;
  }

  .fullscreen-image-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px 20px;
  }

  .fullscreen-image {
    max-width: 100%;
    max-height: calc(100% - 100px);
    object-fit: contain;
    border-radius: 8px;
  }

  .fullscreen-close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(0, 0, 0, 0.7);
    border: none;
    border-radius: 50%;
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: background 0.2s ease;
  }

  .fullscreen-close-btn:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  .fullscreen-image-label {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    text-align: center;
    max-width: 80%;
  }
</style>

